<template>
  <div class="auth-risk-container">
    <!-- 背景装饰 -->
    <div class="leftSider">
      <img src="@assets/leftSider.png" alt="">
    </div>
    <div class="rightSider">
      <img src="@assets/rightSider.png" alt="">
    </div>

    <!-- Header -->
    <dp-header :title="'权威侧域名配置与安全监控'"></dp-header>

    <div class="page-update-time">数据更新时间：{{ currentDateText }}</div>

    <div class="org-search-panel">
      <el-input
            v-model="orgSearchKeyword"
            placeholder="输入机构名称查询"
            size="small"
            @keyup.enter.native="handleOrgSearch"
            clearable
            @clear="onOrgSearchClear"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleOrgSearch" />
      </el-input>
      <el-button
        v-if="hasOrgSearched"
        class="back-overview-btn"
        size="small"
        type="warning"
        icon="el-icon-back"
        @click="backToOverview"
      >返回总览</el-button>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content" :class="{ 'org-search-content': hasOrgSearched }">
      <!-- 左侧：组织机构权威侧风险监控表格 -->
      <div class="left-section">
        <!-- 总览模式 -->
        <div v-if="!hasOrgSearched" class="section-card">
          <div class="card-title">
            <span class="title-main">
              组织机构权威侧风险监控
              <el-tooltip
                content="监控范围：重要组织机构域名"
                placement="top"
                :open-delay="200"
                popper-class="scope-tooltip"
                effect="dark"
              >
                <i class="el-icon-info scope-tip-icon"></i>
              </el-tooltip>
            </span>
            <span class="subtitle" v-if="orgRiskTotal > 0">（显示 <span class="highlight">{{ filteredOrgRiskData.length }}</span> / {{ orgRiskTotal }} 家机构）</span>
          </div>
          <div class="org-table-tools">
            <el-select
              v-model="orgProvinceFilter"
              size="mini"
              filterable
              placeholder="省份"
              @change="handleOrgProvinceFilterChange"
            >
              <el-option label="全国" value="all"></el-option>
              <el-option
                v-for="province in provinceFilterOptions"
                :key="province"
                :label="province"
                :value="province"
              ></el-option>
            </el-select>
            <el-select
              v-model="orgRiskStatusFilter"
              size="mini"
              placeholder="风险状态"
              @change="handleOrgFilterChange"
            >
              <el-option label="全部状态" value="all"></el-option>
              <el-option label="有配置风险" value="hasRisk"></el-option>
              <el-option label="有劫持事件" value="hasHijack"></el-option>
              <el-option label="无风险记录" value="noRisk"></el-option>
            </el-select>
            <el-select
              v-model="orgDomainScaleFilter"
              size="mini"
              placeholder="域名规模"
              @change="handleOrgFilterChange"
            >
              <el-option label="全部规模" value="all"></el-option>
              <el-option label="无域名" value="none"></el-option>
              <el-option label="1-9 个域名" value="small"></el-option>
              <el-option label="10-49 个域名" value="medium"></el-option>
              <el-option label="50 个以上" value="large"></el-option>
            </el-select>
            <el-select
              v-model="orgSortMode"
              size="mini"
              placeholder="排序"
              @change="handleOrgFilterChange"
            >
              <el-option label="默认排序" value="chineseRisk"></el-option>
              <el-option label="配置风险降序" value="riskDesc"></el-option>
              <el-option label="域名数量降序" value="domainDesc"></el-option>
              <el-option label="劫持事件降序" value="hijackDesc"></el-option>
              <el-option label="机构名称升序" value="nameAsc"></el-option>
            </el-select>
            <el-button
              size="mini"
              plain
              icon="el-icon-refresh-left"
              @click="resetOrgTableFilters"
            ></el-button>
            <el-input
              v-model="orgNameFilter"
              size="mini"
              clearable
              placeholder="机构关键词"
              class="org-keyword-input"
              @input="handleOrgKeywordFilterInput"
              @clear="handleOrgFilterChange"
            ></el-input>
          </div>
          <el-table
            ref="orgRiskTable"
            :data="pagedOrgRiskData"
            v-loading="orgRiskLoading"
            style="width: 100%"
            class="org-risk-table"
            @row-click="handleOrgClick"
          >
            <el-table-column prop="org" label="组织机构" width="40%"></el-table-column>
            <el-table-column prop="domainCount" label="域名数量" width="20%"></el-table-column>
            <el-table-column prop="riskCount" label="配置风险" width="20%"></el-table-column>
            <el-table-column prop="hijackCount" label="劫持事件" width="20%"></el-table-column>
          </el-table>
          <!-- 分页控件 -->
          <el-pagination
            v-if="filteredOrgRiskData.length > orgPageSize"
            small
            layout="prev, pager, next"
            :current-page="orgCurrentPage"
            :page-size="orgPageSize"
            :total="filteredOrgRiskData.length"
            @current-change="handleOrgPageChange"
            class="org-pagination"
          />
        </div>

        <!-- 机构搜索模式：同样用表格，只显示该机构一行 -->
        <div v-else class="section-card">
          <div class="card-title">
            <span class="title-main">
              组织机构权威侧风险监控
              <el-tooltip
                content="监控范围：重要组织机构域名"
                placement="top"
                :open-delay="200"
                popper-class="scope-tooltip"
                effect="dark"
              >
                <i class="el-icon-info scope-tip-icon"></i>
              </el-tooltip>
            </span>
            <span class="subtitle">（已筛选：<span class="highlight">{{ searchedOrgName }}</span>）</span>
          </div>
          <el-table
            :data="searchedOrgTableRow"
            style="width: 100%"
            class="org-risk-table"
          >
            <el-table-column prop="org" label="组织机构" width="40%"></el-table-column>
            <el-table-column prop="domainCount" label="域名数量" width="20%"></el-table-column>
            <el-table-column prop="riskCount" label="配置风险" width="20%"></el-table-column>
            <el-table-column prop="hijackCount" label="劫持事件" width="20%"></el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 中间：中国地图 -->
      <div class="center-section">
        <div class="section-card">
          <div class="card-title">
            <span class="title-main">
              {{ hasOrgSearched ? (searchedOrgName + ' 的地理位置') : '重要组织机构分布' }}
              <el-tooltip
                content="监控范围：重要组织机构域名"
                placement="top"
                :open-delay="200"
                popper-class="scope-tooltip"
                effect="dark"
              >
                <i class="el-icon-info scope-tip-icon"></i>
              </el-tooltip>
            </span>
            <el-button
              v-if="mapViewLevel === 'province' && !hasOrgSearched"
              type="primary"
              size="mini"
              @click="backToChinaView"
              class="back-button"
            >
              返回全国
            </el-button>
          </div>
          <div v-if="provinceLoading" class="map-loading-bar">
            <div class="map-loading-progress" :style="{ width: provinceLoadingProgress + '%' }"></div>
          </div>
          <div class="map-container" ref="mapContainer"></div>
        </div>
      </div>

      <!-- 右侧：域名配置与安全监控统计 -->
      <div class="right-section">
        <!-- 总览模式 -->
        <div v-if="!hasOrgSearched" class="section-card">
          <div class="card-title">
            <span class="title-main">
              域名配置与安全监控统计
              <el-tooltip
                content="监控范围：重要组织机构域名"
                placement="top"
                :open-delay="200"
                popper-class="scope-tooltip"
                effect="dark"
              >
                <i class="el-icon-info scope-tip-icon"></i>
              </el-tooltip>
            </span>
          </div>
          <div class="stats-cards-container">
            <div
              v-for="(item, index) in statsCardData"
              :key="index"
              class="stats-card-item"
              :class="'risk-level-' + item.level"
            >
              <div class="stats-card-header">
                <span class="risk-icon" :class="'icon-' + item.level">
                  <i :class="item.icon"></i>
                </span>
                <el-tooltip
                  :content="item.description"
                  placement="left"
                  :open-delay="200"
                  popper-class="risk-desc-tooltip"
                  effect="dark"
                >
                  <div class="risk-name">{{ item.name }}</div>
                </el-tooltip>
              </div>
              <div class="stats-card-body">
                <div class="risk-count-wrapper">
                  <div class="risk-count">{{ item.count }}</div>
                  <div class="risk-label">{{ item.label }}</div>
                </div>
                <span class="risk-level-badge">{{ item.levelText }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 机构搜索模式 -->
        <div v-else class="section-card">
          <div class="card-title">{{ searchedOrgName }} 风险统计</div>
          <el-table
            :data="orgRiskStatsData"
            style="width: 100%"
            class="org-stats-table"
          >
            <el-table-column prop="riskSource" label="风险来源" min-width="110"></el-table-column>
            <el-table-column prop="riskLevel" label="风险等级" min-width="120">
              <template slot-scope="scope">
                <span :class="getRiskClass(scope.row.riskLevel)">{{ scope.row.riskLevel }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="domainCount" label="二级域名" min-width="80"></el-table-column>
            <el-table-column prop="subdomainCount" label="子域名" min-width="75"></el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 底部：域名权威侧资源记录劫持事件通报 / 机构域名资产 -->
      <div class="bottom-section">

        <!-- 总览模式：劫持事件通报 -->
        <div v-if="!hasOrgSearched" class="section-card bottom-card">
          <div class="card-title">
            <span class="title-main">
              域名权威侧资源记录劫持事件通报
              <el-tooltip
                content="监控范围：重要组织机构域名、全球高访问域名、全球政府机构域名"
                placement="top"
                :open-delay="200"
                popper-class="scope-tooltip"
                effect="dark"
              >
                <i class="el-icon-info scope-tip-icon"></i>
              </el-tooltip>
            </span>
            <div class="hijack-time-filter">
              <el-button
                v-for="item in hijackTimeFilters"
                :key="item.value"
                size="mini"
                :type="hijackTimeFilter === item.value ? 'primary' : ''"
                plain
                @click="setHijackTimeFilter(item.value)"
              >{{ item.label }}</el-button>
            </div>
          </div>

          <div class="bottom-content">
            <!-- 左侧：统计信息 -->
            <div class="stats-panel">
              <div class="stats-title">劫持事件统计</div>

              <div class="stats-items-wrapper">
                <div
                  v-for="item in hijackPeriodStats"
                  :key="item.label"
                  class="stats-item hijack-period-item"
                >
                  <div class="stats-label">{{ item.label }}</div>
                  <div class="stats-value">
                    <span class="number">{{ item.count }}</span>
                    <span class="unit">次</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧：事件表格 -->
            <div class="events-panel">
              <el-table
                ref="hijackEventTable"
                :data="filteredHijackEventData"
                style="width: 100%"
                class="hijack-event-table"
              >
                <el-table-column prop="time" label="发生时间" min-width="150"></el-table-column>
                <el-table-column prop="domain" label="被劫持域名" min-width="180"></el-table-column>
                <el-table-column label="归属机构" min-width="180">
                  <template slot-scope="scope">
                    <template v-if="scope.row.companiesLoading"><span class="muted">查询中...</span></template>
                    <template v-else-if="(scope.row.companies || []).length">
                      <span v-for="(company, index) in scope.row.companies" :key="index" class="company-tag">{{ company }}</span>
                    </template>
                    <template v-else><span class="muted">—</span></template>
                  </template>
                </el-table-column>
                <el-table-column label="域名归属列表" min-width="160">
                  <template slot-scope="scope">
                    <template v-if="scope.row.listMetadataLoading"><span class="muted">查询中...</span></template>
                    <template v-else-if="formatDomainListMetadata(scope.row.listMetadata) !== '—'">
                      <el-tooltip :content="formatDomainListMetadata(scope.row.listMetadata)" placement="top" :open-delay="200">
                        <span class="list-tags-preview">{{ formatDomainListMetadata(scope.row.listMetadata) }}</span>
                      </el-tooltip>
                    </template>
                    <template v-else><span class="muted">—</span></template>
                  </template>
                </el-table-column>
                <el-table-column label="排名/归属" min-width="130">
                  <template slot-scope="scope">
                    <template v-if="scope.row.listMetadataLoading"><span class="muted">查询中...</span></template>
                    <span v-else>{{ formatBestListRank(scope.row.listMetadata) }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="normalIP" label="劫持前IP" min-width="130">
                  <template slot-scope="scope">
                    <span style="white-space: pre-line;">{{ scope.row.normalIP }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="abnormalIP" label="劫持后IP" min-width="130">
                  <template slot-scope="scope">
                    <span style="white-space: pre-line;">{{ scope.row.abnormalIP }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="confidenceValue" label="置信度" min-width="130">
                  <template slot-scope="scope">
                    <el-tooltip
                      :content="scope.row.confidenceLabel"
                      placement="top"
                      :open-delay="300"
                      effect="dark"
                    >
                      <div class="confidence-bar">
                        <span
                          class="confidence-fill"
                          :class="scope.row.confidenceClass"
                          :style="{ width: scope.row.confidenceWidth }"
                        ></span>
                      </div>
                    </el-tooltip>
                  </template>
                </el-table-column>
                <el-table-column prop="purpose" label="劫持行为和目的智能分析" min-width="340">
                  <template slot-scope="scope">
                    <el-tooltip
                      placement="top-start"
                      :open-delay="300"
                      popper-class="hijack-purpose-tooltip"
                      effect="dark"
                    >
                      <div slot="content" class="hijack-purpose-tooltip-content">
                        {{ scope.row.purpose }}
                      </div>
                      <ul class="purpose-list">
                        <li
                          v-for="(item, index) in scope.row.purposeItems"
                          :key="index"
                        >{{ item }}</li>
                      </ul>
                    </el-tooltip>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- 机构搜索模式：域名资产 -->
        <div v-else class="section-card bottom-card org-search-bottom">
          <div class="card-title">
            {{ searchedOrgName }} 域名资产
            <span class="subtitle">（共 <span class="highlight">{{ orgDomainTotal }}</span> 个域名）</span>
          </div>

          <el-table
            :data="pagedOrgDomainList"
            style="width: 100%"
            class="org-domain-table"
            @row-click="handleOrgDomainClick"
            :row-class-name="() => 'clickable-row'"
          >
            <el-table-column prop="domain" label="域名" min-width="180"></el-table-column>
            <el-table-column label="原归属机构" min-width="180">
              <template slot-scope="scope">
                <span v-if="scope.row.companies === null" class="company-loading">查询中...</span>
                <span v-else-if="scope.row.companies.length === 0" class="company-none">—</span>
                <span v-else class="company-tags">
                  <el-tag
                    v-for="(c, i) in scope.row.companies"
                    :key="i"
                    size="mini"
                    type="info"
                    class="company-tag"
                  >{{ c }}</el-tag>
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="severe" label="严重配置风险警告" min-width="140">
              <template slot-scope="scope">
                <span class="risk-count-tag severe">{{ scope.row.severe }}条</span>
              </template>
            </el-table-column>
            <el-table-column prop="important" label="重要配置风险告警" min-width="140">
              <template slot-scope="scope">
                <span class="risk-count-tag important">{{ scope.row.important }}条</span>
              </template>
            </el-table-column>
            <el-table-column prop="general" label="一般配置提示" min-width="140">
              <template slot-scope="scope">
                <span class="risk-count-tag general">{{ scope.row.general }}条</span>
              </template>
            </el-table-column>
            <el-table-column prop="hijack" label="历史域名劫持事件" min-width="140">
              <template slot-scope="scope">
                <span class="risk-count-tag hijack">{{ scope.row.hijack }}次</span>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            v-if="orgDomainTotal > 0"
            @current-change="handleOrgDomainPageChange"
            :current-page="orgDomainCurrentPage"
            :page-size="orgDomainPageSize"
            layout="total, prev, pager, next"
            :total="orgDomainTotal"
            class="org-pagination"
          />
        </div>
      </div>
      <footer class="beian-footer">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">鲁ICP备2026032922号-1</a>
        <span>|</span>
        <a
          class="beian-police-link"
          href="https://beian.mps.gov.cn/#/query/webSearch?code=37140202001764"
          rel="noreferrer"
          target="_blank"
        >
          <img class="beian-police-logo" src="/beian-police.png" alt="公安备案图标" />
          <span>鲁公网安备37140202001764号</span>
        </a>
      </footer>
    </div>

    <!-- 域名风险详情弹窗 -->
    <el-dialog
      :title="`域名 ${orgSelectedDomain} 的风险详情`"
      :visible.sync="orgDialogVisible"
      :modal="false"
      width="80%"
      class="risk-detail-dialog"
    >
      <div v-if="orgDomainDetail" class="risk-detail-content">
        <div v-if="orgDomainDetail.严重配置风险警告 && orgDomainDetail.严重配置风险警告.length > 0" class="risk-category">
          <div class="category-title severe-title">严重配置风险警告（{{ orgDomainDetail.严重配置风险警告.length }}条）</div>
          <div v-for="(risk, index) in orgDomainDetail.严重配置风险警告" :key="'s-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>
        <div v-if="orgDomainDetail.重要配置风险告警 && orgDomainDetail.重要配置风险告警.length > 0" class="risk-category">
          <div class="category-title important-title">重要配置风险告警（{{ orgDomainDetail.重要配置风险告警.length }}条）</div>
          <div v-for="(risk, index) in orgDomainDetail.重要配置风险告警" :key="'i-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>
        <div v-if="orgDomainDetail.一般配置风险提示 && orgDomainDetail.一般配置风险提示.length > 0" class="risk-category">
          <div class="category-title general-title">一般配置提示（{{ orgDomainDetail.一般配置风险提示.length }}条）</div>
          <div v-for="(risk, index) in orgDomainDetail.一般配置风险提示" :key="'g-' + index" class="risk-item">
            <div class="risk-type">{{ risk.风险类型 }}</div>
            <div class="risk-location">发生位置：{{ risk.发生位置 }}</div>
            <div class="risk-explanation">{{ risk.解释 }}</div>
          </div>
        </div>
        <div v-if="orgDomainDetail.域名劫持事件 && orgDomainDetail.域名劫持事件.length > 0" class="risk-category">
          <div class="category-title hijack-title">域名劫持事件（{{ orgDomainDetail.域名劫持事件.length }}次）</div>
          <div v-for="(event, index) in orgDomainDetail.域名劫持事件" :key="'h-' + index" class="hijack-item">
            <div class="hijack-time">发生时间：{{ event.发生时间 }}</div>
            <div class="hijack-ips">
              <span class="ip-label">正常IP:</span> {{ event.正常IP }}
              <span class="ip-label" style="margin-left: 20px;">异常IP:</span> {{ event.异常IP }}
            </div>
            <div class="hijack-analysis">
              <div><strong>HTTPS分析:</strong> {{ event.HTTPS分析 }}</div>
              <div><strong>DNS/TLS分析:</strong> {{ event['DNS/TLS分析'] }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 查询加载遮罩 -->
    <div v-if="isOrgLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-dots">
          <span></span><span></span><span></span>
        </div>
        <div class="loading-text">{{ orgLoadingText }}</div>
      </div>
    </div>

    <!-- 底部导航 -->
    <navigation ref="nav" @navChange="handleNavChange"></navigation>

  </div>
</template>

<script>
import * as echarts from "echarts";
import axios from "axios";
import chinaMapData from "@/assets/models/geoJson/china.json";
import { getRegistrableDomain } from "@/utils/domain";
import { formatDomainListMetadata, formatBestListRank } from "@/utils/domainListMetadata";

import {
  getOrgAuthRisk,
  getOrgAuthRiskMap,
  getOrgAuthRiskStatistic,
  getDomainAuthHijackList,
  getDomainAuthRiskMapDetail,
  getEntityInfo,
  getDomainAuthRisk,
  getDomainAuthRiskList,
  getDomainAuthRiskDetail,
  getDomainCompanyBatch,
  getDomainListMetadata
} from "@/service/dashboard";

// 各省边界框（minLng,minLat,maxLng,maxLat），用于天地图 v2/search mapBound 参数
const PROVINCE_BOUNDS = {
  "北京市":          "115.42,39.44,117.51,41.06",
  "上海市":          "120.85,30.68,122.00,31.88",
  "天津市":          "116.72,38.57,118.06,40.25",
  "重庆市":          "105.29,28.17,110.19,32.19",
  "黑龙江省":        "121.18,43.43,135.09,53.56",
  "吉林省":          "121.63,40.87,131.29,44.83",
  "辽宁省":          "118.84,38.71,125.67,43.49",
  "内蒙古自治区":    "97.19,37.41,126.05,53.33",
  "河北省":          "113.27,36.06,119.83,42.62",
  "山西省":          "110.23,34.58,114.56,40.74",
  "陕西省":          "105.49,31.71,111.24,39.59",
  "宁夏回族自治区":  "104.28,35.14,107.65,39.38",
  "甘肃省":          "92.31,32.59,108.69,42.80",
  "青海省":          "89.35,31.37,103.07,39.19",
  "新疆维吾尔自治区": "73.45,34.25,96.40,49.18",
  "西藏自治区":      "78.40,26.86,99.10,36.49",
  "四川省":          "97.35,26.04,108.55,34.32",
  "云南省":          "97.53,21.13,106.20,29.26",
  "贵州省":          "103.60,24.62,109.60,29.22",
  "广西壮族自治区":  "104.47,20.54,112.05,26.39",
  "海南省":          "108.62,18.16,111.05,20.16",
  "广东省":          "109.67,20.22,117.31,25.52",
  "福建省":          "115.86,23.53,120.74,28.32",
  "浙江省":          "117.97,27.04,122.92,31.19",
  "江西省":          "113.57,24.48,118.49,30.08",
  "湖南省":          "108.79,24.63,114.26,30.13",
  "湖北省":          "108.36,29.03,116.14,33.28",
  "河南省":          "110.36,31.38,116.65,36.38",
  "山东省":          "114.81,34.38,122.72,38.41",
  "江苏省":          "116.36,30.77,121.96,35.13",
  "安徽省":          "114.90,29.41,119.65,34.64",
  "台湾省":          "119.30,21.90,122.00,25.30",
  "香港特别行政区":  "113.83,22.15,114.44,22.57",
  "澳门特别行政区":  "113.53,22.11,113.60,22.22"
};

export default {
  name: "AuthRisk",
  data() {
    return {
      // 组织机构风险数据
      orgRiskData: [],
      orgProvinceFilter: "all",
      orgNameFilter: "",
      orgRiskStatusFilter: "all",
      orgDomainScaleFilter: "all",
      orgSortMode: "chineseRisk",
      appliedOrgNameFilter: "",
      appliedOrgRiskStatusFilter: "all",
      appliedOrgDomainScaleFilter: "all",
      appliedOrgSortMode: "chineseRisk",
      orgRiskLoading: false,
      orgFilterTimer: null,
      orgFilterLoadingTimer: null,
      // 组织机构总数
      orgRiskTotal: 0,
      // 组织机构分页
      orgCurrentPage: 1,
      orgPageSize: 10, // 调整为10条，刚好填满容器高度

      // 地理编码相关
      geocodedOrgs: [], // 已获取坐标的机构列表 [{name, lng, lat, ...orgData}]
      geocodingInProgress: false, // 是否正在进行地理编码

      // 地图实例
      mapChart: null,

      // 统计卡片数据
      statsCardData: [],

      // 地图视图状态
      selectedProvince: null, // 当前选中的省份
      provinceOrgList: [], // 省份内的机构列表
      mapViewLevel: "china", // 地图视图层级：'china' 或 'province'
      provinceLoading: false,
      provinceLoadingProgress: 0,

      // 劫持事件统计
      hijackStats: {
        eventCount: 0,
        domainCount: 0,
        orgCount: 0
      },

      // 劫持事件数据
      hijackEventData: [],
      hijackTimeFilter: "all",
      hijackTimeFilters: [
        { label: "全部", value: "all" },
        { label: "近3个月", value: "3m" },
        { label: "近1个月", value: "1m" },
        { label: "近1周", value: "1w" }
      ],

      // resize防抖定时器
      resizeTimer: null,

      // 机构搜索相关
      orgSearchKeyword: "",
      searchedOrgName: "",
      hasOrgSearched: false,

      // 机构搜索结果
      searchedEntityInfo: { 实体规范名称: "", 二级域名数量: 0, 子域名数量: 0 },
      orgRiskStatsData: [],
      orgCompanyRiskRows: [],
      orgDomainListData: [],
      orgDomainCurrentPage: 1,
      orgDomainPageSize: 20,
      orgDomainTotal: 0,

      // 域名详情弹窗
      orgDialogVisible: false,
      orgSelectedDomain: "",
      orgDomainDetail: null,

      // 机构搜索 loading
      isOrgLoading: false,
      orgLoadingText: "",
      orgLoadingTimer: null,
      orgLoadingSteps: [
        "Thinking...",
        "Crawling domain assets...",
        "Analyzing risk signals...",
        "Correlating threat data...",
        "Scanning DNS records...",
        "Evaluating security posture...",
        "Almost there..."
      ],
      orgLoadingStepIndex: 0
    };
  },
  computed: {
    currentDateText() {
      // 固定为本周周一的日期
      const now = new Date();
      const offset = (now.getDay() + 6) % 7; // 距本周周一的天数（周日=6）
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
      const pad = value => String(value).padStart(2, "0");

      return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
    },
    provinceFilterOptions() {
      return Object.keys(PROVINCE_BOUNDS);
    },
    filteredOrgRiskData() {
      const keyword = this.appliedOrgNameFilter.trim().toLowerCase();

      return this.orgRiskData.filter(row => {
        const orgName = String(row.org || "").toLowerCase();
        const domainCount = this.normalizeNumber(row.domainCount);
        const riskCount = this.normalizeNumber(row.riskCount);
        const hijackCount = this.normalizeNumber(row.hijackCount);

        if (keyword && !orgName.includes(keyword)) {
          return false;
        }
        if (this.appliedOrgRiskStatusFilter === "hasRisk" && riskCount <= 0) {
          return false;
        }
        if (this.appliedOrgRiskStatusFilter === "hasHijack" && hijackCount <= 0) {
          return false;
        }
        if (this.appliedOrgRiskStatusFilter === "noRisk" && (riskCount !== 0 || hijackCount !== 0)) {
          return false;
        }
        if (this.appliedOrgDomainScaleFilter === "none" && domainCount !== 0) {
          return false;
        }
        if (this.appliedOrgDomainScaleFilter === "small" && (domainCount <= 0 || domainCount >= 10)) {
          return false;
        }
        if (this.appliedOrgDomainScaleFilter === "medium" && (domainCount < 10 || domainCount >= 50)) {
          return false;
        }
        if (this.appliedOrgDomainScaleFilter === "large" && domainCount < 50) {
          return false;
        }
        return true;
      });
    },
    sortedOrgRiskData() {
      return this.sortOrgRows(this.filteredOrgRiskData);
    },
    filteredHijackEventData() {
      const cutoff = this.getHijackTimeCutoff(this.hijackTimeFilter);

      if (!cutoff) {
        return this.hijackEventData;
      }
      return this.hijackEventData.filter(item => item.timeValue && item.timeValue >= cutoff.getTime());
    },
    hijackPeriodStats() {
      return [
        { label: "全部", count: this.countHijackEventsByPeriod("all") },
        { label: "近3个月", count: this.countHijackEventsByPeriod("3m") },
        { label: "近1个月", count: this.countHijackEventsByPeriod("1m") },
        { label: "近1周", count: this.countHijackEventsByPeriod("1w") }
      ];
    },
    // 分页后的组织机构数据
    pagedOrgRiskData() {
      const start = (this.orgCurrentPage - 1) * this.orgPageSize;
      const end = start + this.orgPageSize;

      return this.sortedOrgRiskData.slice(start, end);
    },
    // 分页后的机构域名列表
    pagedOrgDomainList() {
      const start = (this.orgDomainCurrentPage - 1) * this.orgDomainPageSize;

      return this.orgDomainListData.slice(start, start + this.orgDomainPageSize);
    },
    // 机构搜索模式下左侧表格数据（单行）
    searchedOrgTableRow() {
      if (!this.hasOrgSearched) {
        return [];
      }
      return this.orgCompanyRiskRows;
    }
  },
  mounted() {
    console.log("[DEBUG] mounted 开始");
    // 使用$nextTick确保DOM完全渲染后再初始化图表
    this.$nextTick(() => {
      // 延迟50ms，等待CSS Grid布局完全稳定
      setTimeout(async () => {
        console.log("[DEBUG] 开始初始化图表");
        this.initCharts();
        console.log("[DEBUG] 图表初始化完成，开始加载数据");
        await this.loadData();
        console.log("[DEBUG] 数据加载完成");

        // 数据加载完成后调用resize，确保图表尺寸正确
        this.$nextTick(() => {
          if (this.mapChart) {
            this.mapChart.resize();
          }

          console.log("[DEBUG] mounted 所有操作完成");
        });
      }, 50);
    });

    // 添加窗口resize事件监听
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    if (this.mapChart) {
      this.mapChart.dispose();
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.orgFilterTimer) {
      clearTimeout(this.orgFilterTimer);
    }
    if (this.orgFilterLoadingTimer) {
      clearTimeout(this.orgFilterLoadingTimer);
    }
    // 移除resize事件监听
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    getChinaMapLayout() {
      return {
        layoutCenter: ["50%", "70%"],
        layoutSize: "115%"
      };
    },

    // 处理窗口大小变化（带防抖）
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.mapChart) {
          this.mapChart.resize();
        }
      }, 100); // 100ms防抖
    },
    // 初始化图表
    initCharts() {
      // 注册中国地图
      echarts.registerMap("china", chinaMapData);

      // 初始化地图
      this.mapChart = echarts.init(this.$refs.mapContainer);

      // 地图点击事件
      this.mapChart.on("click", (params) => {
        if (params.componentType === "series") {
          this.handleProvinceClick(params.name);
        }
      });
    },

    // 加载所有数据
    async loadData() {
      console.log("[DEBUG] loadData 开始");
      try {
        await Promise.all([
          this.loadOrgRiskData(),
          this.loadMapData(),
          this.loadStatsData(),
          this.loadHijackData()
        ]);
        console.log("[DEBUG] loadData 完成");
      } catch (error) {
        console.error("[DEBUG] loadData 出错:", error);
      }
    },

    // 加载组织机构风险数据
    async loadOrgRiskData() {
      console.log("[DEBUG] loadOrgRiskData 开始");
      this.orgRiskLoading = true;
      try {
        const res = await getOrgAuthRisk();

        console.log("[DEBUG] loadOrgRiskData API返回:", res);
        // 真实API直接返回数据，不包含 code/data 包装
        const data = res.code === 200 ? res.data : res;
        // 确保数据存在且格式正确

        if (data && data.组织机构 && Array.isArray(data.组织机构)) {
          this.orgRiskData = data.组织机构.map((org, index) => this.toOrgRiskRow({
            org,
            domainCount: data.域名数量?.[index],
            riskCount: data.配置风险域名数量?.[index],
            hijackCount: data.劫持事件域名数量?.[index]
          }));
          this.orgRiskTotal = this.orgRiskData.length;
          this.orgCurrentPage = 1; // 重置到第一页
        }
        console.log("[DEBUG] loadOrgRiskData 完成, 数据条数:", this.orgRiskData.length);
      } catch (error) {
        console.error("[DEBUG] loadOrgRiskData 出错:", error);
        this.orgRiskData = [];
        this.orgRiskTotal = 0;
      } finally {
        this.orgRiskLoading = false;
      }
    },

    // 加载地图数据
    async loadMapData() {
      console.log("[DEBUG] loadMapData 开始");
      try {
        const res = await getOrgAuthRiskMap();

        console.log("[DEBUG] loadMapData API返回:", res);
        // 真实API直接返回数据，不包含 code/data 包装
        const data = res.code === 200 ? res.data : res;

        console.log("[DEBUG] loadMapData 处理后数据:", data);
        if (data && typeof data === "object") {

          // 中国省份名称列表（与 echarts 地图 JSON 中的名称一致）
          const chinaProvinces = [
            "北京市", "天津市", "上海市", "重庆市",
            "河北省", "山西省", "辽宁省", "吉林省", "黑龙江省",
            "江苏省", "浙江省", "安徽省", "福建省", "江西省", "山东省",
            "河南省", "湖北省", "湖南省", "广东省", "海南省",
            "四川省", "贵州省", "云南省", "陕西省", "甘肃省", "青海省",
            "台湾省", "内蒙古自治区", "广西壮族自治区", "西藏自治区",
            "宁夏回族自治区", "新疆维吾尔自治区", "香港特别行政区", "澳门特别行政区"
          ];

          // API 返回的简称映射到 echarts 地图使用的全称
          const toEchartsName = {
            "香港": "香港特别行政区",
            "澳门": "澳门特别行政区",
            "台湾": "台湾省"
          };

          // 过滤并映射中国省份数据（使用 Map 合并重复项）
          const mapDataMap = new Map();

          Object.keys(data).forEach(name => {
            // 先尝试转换简称
            const echartsName = toEchartsName[name] || name;
            // 检查是否是中国省份

            if (chinaProvinces.includes(echartsName)) {
              // 合并相同省份的数据
              if (mapDataMap.has(echartsName)) {
                mapDataMap.set(echartsName, mapDataMap.get(echartsName) + data[name]);
              } else {
                mapDataMap.set(echartsName, data[name]);
              }
            }
          });

          // 转换为数组格式
          const mapData = Array.from(mapDataMap, ([name, value]) => ({ name, value }));

          console.log("[DEBUG] loadMapData 转换后地图数据:", mapData);

          // 如果没有中国数据，使用默认数据
          if (mapData.length === 0) {
            console.warn("[DEBUG] 真实API返回的地图数据不包含中国省份，使用默认数据");
            this.updateMapChart([
              { name: "北京市", value: 100 },
              { name: "上海市", value: 80 },
              { name: "广东省", value: 120 }
            ]);
          } else {
            console.log("[DEBUG] loadMapData 最终数据条数:", mapData.length);
            this.updateMapChart(mapData);
          }
        }
        console.log("[DEBUG] loadMapData 完成");
      } catch (error) {
        console.error("[DEBUG] loadMapData 出错:", error);
        // 加载失败时使用空数据，避免页面卡住
        this.updateMapChart([]);
      }
    },

    // 更新地图图表
    updateMapChart(data) {
      console.log("[DEBUG] updateMapChart 被调用, 数据:", data);
      console.log("[DEBUG] mapChart 实例:", this.mapChart);

      // 计算最大值，确保返回有效数字
      const values = data.map(d => d.value).filter(v => typeof v === "number" && !isNaN(v));
      const maxValue = values.length > 0 ? Math.max(...values) : 100;

      console.log("[DEBUG] updateMapChart maxValue:", maxValue);

      const option = {
        tooltip: {
          trigger: "item",
          formatter: "{b}<br/>机构数量: {c}"
        },
        visualMap: {
          min: 0,
          max: Math.max(maxValue, 100),
          text: ["高", "低"],
          realtime: false,
          calculable: true,
          inRange: {
            color: ["#e0ffff", "#006edd"]
          },
          textStyle: {
            color: "#fff"
          },
          bottom: 20,
          left: 20
        },
        geo: {
          show: false,
          map: "china",
          roam: true,
          ...this.getChinaMapLayout(),
          itemStyle: {
            areaColor: "#1e3a8a",
            borderColor: "#4da6ff",
            borderWidth: 1
          },
          emphasis: {
            itemStyle: {
              areaColor: "#40a9ff",
              borderColor: "#fff",
              borderWidth: 2
            },
            label: {
              show: true,
              color: "#fff"
            }
          },
          label: {
            show: false
          }
        },
        series: [
          {
            name: "机构数量",
            type: "map",
            map: "china",
            roam: true,
            ...this.getChinaMapLayout(),
            itemStyle: {
              areaColor: "#1e3a8a",
              borderColor: "#4da6ff",
              borderWidth: 1
            },
            emphasis: {
              itemStyle: {
                areaColor: "#40a9ff",
                borderColor: "#fff",
                borderWidth: 2
              },
              label: {
                show: true,
                color: "#fff"
              }
            },
            label: {
              show: false
            },
            data: data
          }
        ]
      };

      console.log("[DEBUG] updateMapChart 设置 option");
      this.mapChart.setOption(option);
      console.log("[DEBUG] updateMapChart 完成");
    },

    // 加载统计数据
    async loadStatsData() {
      console.log("[DEBUG] loadStatsData 开始");
      try {
        const res = await getOrgAuthRiskStatistic();

        console.log("[DEBUG] loadStatsData API返回:", res);
        if (res.code === 200 && res.data) {
          this.updateStatsCards(res.data);
        }
        console.log("[DEBUG] loadStatsData 完成");
      } catch (error) {
        console.error("[DEBUG] loadStatsData 出错:", error);
        // 使用空数据避免页面卡住
        this.updateStatsCards({});
      }
    },

    // 更新统计卡片数据
    updateStatsCards(data) {
      // 风险配置映射：定义每种风险的等级、图标和描述
      const riskConfig = {
        "悬空记录": {
          level: "critical",
          icon: "el-icon-warning",
          label: "域名",
          levelText: "严重风险",
          description: "检测指向已释放、托管于有风险的第三方服务或已经过期的DNS记录,识别潜在的域名劫持风险。"
        },
        "僵尸NS记录": {
          level: "critical",
          icon: "el-icon-warning",
          label: "域名",
          levelText: "严重风险",
          description: "检测域名中是否存在僵尸记录,即在SLD子域权威服务器中存在指向正在运行但拒绝提供服务的 NS 记录"
        },
        "陈旧胶水记录": {
          level: "critical",
          icon: "el-icon-warning",
          label: "域名",
          levelText: "严重风险",
          description: "检测胶水记录(Glue Record)是否与实际IP地址一致,识别过期或错误的胶水记录。"
        },
        "循环授权": {
          level: "high",
          icon: "el-icon-warning-outline",
          label: "域名",
          levelText: "重要风险",
          description: "检测域名解析链中是否存在循环引用问题,即NS/CNAME记录形成闭环。"
        },
        "跛脚委派": {
          level: "high",
          icon: "el-icon-warning-outline",
          label: "域名",
          levelText: "重要风险",
          description: "检测DNS委派中的跛脚(Lame)服务器,即NS记录指向的服务器无法正确应答该域名查询。"
        },
        "NS记录不一致": {
          level: "medium",
          icon: "el-icon-info",
          label: "域名",
          levelText: "一般风险",
          description: "检测父域和子域之间NS记录的一致性,识别委托配置不匹配的情况。"
        },
        "孤儿DNS服务器": {
          level: "medium",
          icon: "el-icon-info",
          label: "域名",
          levelText: "一般风险",
          description: "检测域名的DNS服务器是否存在孤儿服务器,即在父域已经不存在但子域仍然存在的服务器。"
        }
      };

      // 定义风险类型的顺序（按严重程度排序）
      const riskOrder = [
        "悬空记录", "僵尸NS记录", "陈旧胶水记录",
        "循环授权", "跛脚委派",
        "NS记录不一致", "孤儿DNS服务器"
      ];

      // 转换数据为卡片格式，并按预定义顺序排序
      this.statsCardData = riskOrder
        .filter(key => Object.prototype.hasOwnProperty.call(data, key)) // 只保留API返回的风险类型
        .map(key => {
          const config = riskConfig[key];

          return {
            name: key === "悬空记录" ? "悬空NS记录" : key,
            count: data[key] || 0,
            level: config.level,
            icon: config.icon,
            label: config.label,
            levelText: config.levelText,
            description: config.description
          };
        });

      // 如果API返回了配置中没有的风险类型，追加到末尾
      Object.keys(data).forEach(key => {
        if (!riskOrder.includes(key)) {
          this.statsCardData.push({
            name: key,
            count: data[key] || 0,
            level: "medium",
            icon: "el-icon-info",
            label: "域名",
            levelText: "一般风险",
            description: "其他配置风险"
          });
        }
      });
    },

    // 加载劫持事件数据
    async loadHijackData() {
      console.log("[DEBUG] loadHijackData 开始");
      try {
        const res = await getDomainAuthHijackList();

        console.log("[DEBUG] loadHijackData API返回:", res);
        // 真实API可能直接返回数据，也可能包含 code/data 包装
        const data = res && res.code === 200 ? res.data : (res && res.发生时间 ? res : null);

        if (data && data.发生时间) {
          // 计算统计数据
          this.hijackStats.eventCount = data.发生时间.length;
          this.hijackStats.domainCount = data.被劫持的域名 ? new Set(data.被劫持的域名).size : 0;
          // 真实API可能没有影响机构字段，使用置信度字段或默认值
          if (data.影响机构) {
            this.hijackStats.orgCount = new Set(data.影响机构.flatMap(org => org.split("，"))).size;
          } else {
            this.hijackStats.orgCount = "-";
          }

          // 转换表格数据，兼容真实API缺少影响机构字段的情况
          this.hijackEventData = data.发生时间.map((time, index) => {
            const fullPurpose = data.劫持目的 ? data.劫持目的[index] : "-";
            const timeDate = this.parseHijackTime(time);
            const confidenceMeta = this.getConfidenceMeta(data.置信度 ? data.置信度[index] : "-");
            const purposeItems = this.getPurposeItems(fullPurpose);

            return {
              time: this.formatHijackTime(timeDate, time),
              rawTime: time,
              timeValue: timeDate ? timeDate.getTime() : 0,
              domain: data.被劫持的域名 ? data.被劫持的域名[index] : "-",
              normalIP: data.正常IP地址 ? this.formatIPs(data.正常IP地址[index]) : "-",
              abnormalIP: data.异常IP地址 ? this.formatIPs(data.异常IP地址[index]) : "-",
              org: confidenceMeta.label,
              confidenceValue: confidenceMeta.value,
              confidenceWidth: confidenceMeta.width,
              confidenceLabel: confidenceMeta.label,
              confidenceClass: confidenceMeta.className,
              companies: [],
              companiesLoading: true,
              listMetadata: {},
              listMetadataLoading: true,
              purpose: fullPurpose,
              purposeItems,
              purposeDetail: purposeItems.map(item => `- ${item}`).join("\n")
            };
          }).sort((a, b) => b.timeValue - a.timeValue);
          const domains = this.hijackEventData.map(item => item.domain).filter(Boolean);
          this.enrichHijackCompanies(domains);
          this.enrichHijackListMetadata(domains);
          console.log("[DEBUG] loadHijackData 完成, 事件数:", this.hijackEventData.length);
        } else {
          // 没有数据时使用默认值
          this.hijackStats = { eventCount: 0, domainCount: 0, orgCount: "-" };
          this.hijackEventData = [];
          console.log("[DEBUG] loadHijackData 完成, 无数据");
        }
      } catch (error) {
        console.error("[DEBUG] loadHijackData 出错:", error);
        // 加载失败时使用默认值
        this.hijackStats = { eventCount: 0, domainCount: 0, orgCount: "-" };
        this.hijackEventData = [];
      }
    },

    async enrichHijackCompanies(domains) {
      const companyMap = await this.queryDomainCompaniesMap(domains);
      this.hijackEventData = this.hijackEventData.map(row => {
        const key = getRegistrableDomain(row.domain);
        return { ...row, companies: companyMap[key] || [], companiesLoading: false };
      });
    },

    async enrichHijackListMetadata(domains) {
      console.log("[DEBUG] AuthRisk 请求 domain-list-metadata, 域名数:", domains.length);
      const listMetadataMap = await this.queryDomainListMetadataMap(domains);
      this.hijackEventData = this.hijackEventData.map(row => {
        const key = (row.domain || "").toLowerCase().replace(/\.$/, "");
        return { ...row, listMetadata: listMetadataMap[key] || {}, listMetadataLoading: false };
      });
    },

    async queryDomainCompaniesMap(domains) {
      const domainKeys = Array.from(new Set(domains.map(getRegistrableDomain).filter(Boolean)));
      if (!domainKeys.length) return {};
      try {
        const res = await getDomainCompanyBatch(domainKeys);
        const source = (res && (res.result || res.items || res.data)) || {};
        const mapped = {};
        Object.keys(source).forEach(key => {
          mapped[getRegistrableDomain(key)] = source[key];
        });
        return mapped;
      } catch (e) {
        console.warn("[AuthRisk] 劫持事件机构归属查询失败", e.message);
        return {};
      }
    },

    async queryDomainListMetadataMap(domains) {
      const unique = Array.from(new Set(domains.map(d => (d || "").toLowerCase().replace(/\.$/, "")).filter(Boolean)));
      if (!unique.length) return {};
      try {
        const res = await getDomainListMetadata(unique);
        return (res && res.items) || {};
      } catch (e) {
        console.warn("[AuthRisk] 劫持事件榜单信息查询失败", e.message);
        return {};
      }
    },

    formatListTags(tags) {
      return (tags || []).map(tag => typeof tag === "string" ? tag : (tag.label || tag.name || "")).filter(Boolean).join("、") || "—";
    },

    formatDomainListMetadata,
    formatBestListRank,

    // 处理省份点击
    async handleProvinceClick(provinceName) {
      console.log("[DEBUG] handleProvinceClick 省份名称:", provinceName);
      this.provinceLoading = true;
      this.orgRiskLoading = true;
      this.provinceLoadingProgress = 0;
      const progressTimer = setInterval(() => {
        if (this.provinceLoadingProgress < 85) {
          this.provinceLoadingProgress += Math.random() * 12 + 3;
          if (this.provinceLoadingProgress > 85) {
            this.provinceLoadingProgress = 85;
          }
        }
      }, 300);

      try {
        const res = await getDomainAuthRiskMapDetail({ 省份名称: provinceName });

        console.log("[DEBUG] handleProvinceClick API返回:", res);
        // 真实API可能直接返回数组，也可能包含 code/data 包装
        let orgList = [];

        if (res.code === 200 && res.data) {
          // 标准格式: {code: 200, data: {机构列表: [...]}}
          orgList = res.data.机构列表 || res.data || [];
        } else if (Array.isArray(res)) {
          // 直接返回数组格式
          orgList = res;
        } else if (res && typeof res === "object" && !res.code) {
          // 可能直接返回对象 {机构列表: [...]}
          orgList = res.机构列表 || [];
        }

        clearInterval(progressTimer);
        this.provinceLoadingProgress = 100;

        // 如果没有数据，不进行放大操作
        if (!orgList || orgList.length === 0) {
          this.$message.warning(`${provinceName}暂无机构数据`);
          return;
        }

        this.selectedProvince = provinceName;
        this.provinceOrgList = orgList;
        this.mapViewLevel = "province";

        await this.updateMapForProvince(provinceName);
        // 省级地图就绪后再更新表格并触发机构定位，避免散点被地图切换覆盖
        this.updateOrgRiskDataForProvince(provinceName, orgList);

        this.$message.success(`已加载${provinceName}的${orgList.length}个机构数据`);
      } catch (error) {
        clearInterval(progressTimer);
        console.error("加载省份详情失败", error);
        this.$message.error("加载省份详情失败");
      } finally {
        setTimeout(() => {
          this.provinceLoading = false;
          this.provinceLoadingProgress = 0;
          this.orgRiskLoading = false;
        }, 400);
      }
    },

    // 更新左侧表格显示省份机构数据
    updateOrgRiskDataForProvince(provinceName, orgList) {
      this.orgProvinceFilter = provinceName;
      this.orgRiskData = orgList.map(org => this.toOrgRiskRow({
        org: org.机构名称,
        domainCount: org.域名数量,
        riskCount: this.normalizeNumber(org.严重配置风险警告) + this.normalizeNumber(org.重要配置风险告警) + this.normalizeNumber(org.一般配置风险提示),
        hijackCount: org.域名劫持事件
      }));
      this.orgRiskTotal = this.orgRiskData.length;
      this.orgCurrentPage = 1; // 重置到第一页

      // 清除之前的地理编码数据，开始查询新省份的机构坐标
      this.geocodedOrgs = [];
      this.$nextTick(() => {
        this.geocodeCurrentPageOrgs();
      });
    },

    // 更新地图显示省份（无散点图）
    updateMapForProvince(provinceName) {
      // 省份全称到省级地图文件名的映射
      const provinceToMapFile = {
        // 直辖市
        "北京市": "北京",
        "上海市": "上海",
        "天津市": "天津",
        "重庆市": "重庆",
        // 省份（大部分去掉"省"字）
        "河北省": "河北",
        "山西省": "山西",
        "辽宁省": "辽宁",
        "吉林省": "吉林",
        "黑龙江省": "黑龙江",
        "江苏省": "江苏",
        "浙江省": "浙江",
        "安徽省": "安徽",
        "福建省": "福建",
        "江西省": "江西",
        "山东省": "山东",
        "河南省": "河南",
        "湖北省": "湖北",
        "湖南省": "湖南",
        "广东省": "广东",
        "海南省": "海南",
        "四川省": "四川",
        "贵州省": "贵州",
        "云南省": "云南",
        "陕西省": "陕西",
        "甘肃省": "甘肃",
        "青海省": "青海",
        "台湾省": "台湾",
        // 自治区
        "内蒙古自治区": "内蒙古",
        "广西壮族自治区": "广西",
        "西藏自治区": "西藏",
        "宁夏回族自治区": "宁夏",
        "新疆维吾尔自治区": "新疆",
        // 特别行政区
        "香港特别行政区": "香港",
        "澳门特别行政区": "澳门"
      };

      const mapName = provinceToMapFile[provinceName] || provinceName.replace(/省|市|自治区|特别行政区|壮族|回族|维吾尔/g, "");

      console.log("[DEBUG] updateMapForProvince 省份:", provinceName, "-> 地图文件:", mapName);

      // 尝试加载省级地图
      return this.loadProvinceMapOnly(provinceName, mapName);
    },

    // 仅加载省级地图（不显示散点）
    async loadProvinceMapOnly(provinceName, mapName) {
      try {
        // 动态加载省级地图数据
        const provinceMapData = await import(`@/assets/models/geoJson/${mapName}.json`);

        // 注册省级地图
        echarts.registerMap(mapName, provinceMapData.default || provinceMapData);

        // 使用省级地图
        this.mapChart.setOption({
          geo: {
            show: true,
            map: mapName,
            roam: true,
            center: undefined,
            zoom: 1.2,
            itemStyle: {
              areaColor: "#1e3a8a",
              borderColor: "#4da6ff",
              borderWidth: 1
            },
            emphasis: {
              itemStyle: {
                areaColor: "#40a9ff",
                borderColor: "#fff",
                borderWidth: 2
              },
              label: {
                show: true,
                color: "#fff"
              }
            },
            label: {
              show: true,
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: 10
            }
          },
          series: [],
          tooltip: {
            trigger: "item",
            formatter: `${provinceName}<br/>机构数: ${this.provinceOrgList.length}`
          }
        }, true);
      } catch (error) {
        console.warn(`省级地图 ${mapName} 未找到，保持当前视图`, error);
        // 如果省级地图不存在，放大到省份位置
        this.$message.info(`已加载${provinceName}机构数据，请查看左侧表格`);
      }
    },

    // 返回全国视图
    backToChinaView({ reloadMap = true, reloadOrg = true } = {}) {
      this.mapViewLevel = "china";
      this.selectedProvince = null;
      this.provinceOrgList = [];
      this.geocodedOrgs = []; // 清除地理编码数据
      this.orgProvinceFilter = "all";

      // 重新注册全国地图（确保切换回全国地图）
      echarts.registerMap("china", chinaMapData);

      // 重置地图为全国视图，使用 notMerge 确保清除散点
      this.mapChart.setOption({
        geo: {
          show: true,
          map: "china",
          center: null,
          zoom: 1,
          roam: true,
          ...this.getChinaMapLayout(),
          itemStyle: {
            areaColor: "#1e3a8a",
            borderColor: "#4da6ff",
            borderWidth: 1
          },
          emphasis: {
            itemStyle: {
              areaColor: "#40a9ff",
              borderColor: "#fff",
              borderWidth: 2
            },
            label: {
              show: true,
              color: "#fff"
            }
          },
          label: {
            show: false
          }
        },
        series: []
      }, true); // notMerge: true 确保清除散点

      // 按调用场景决定是否重新加载地图和表格数据，避免表格刷新连带地图闪烁
      if (reloadMap) {
        this.loadMapData();
      }
      if (reloadOrg) {
        this.loadOrgRiskData();
      }

      // 重新绑定省份点击事件
      this.mapChart.off("click");
      this.mapChart.on("click", (params) => {
        if (params.componentType === "series" && params.seriesType === "map") {
          this.handleProvinceClick(params.name);
        }
      });
    },

    // 处理组织机构分页切换
    handleOrgPageChange(page) {
      this.orgCurrentPage = page;
      // 滚动表格到顶部
      this.$nextTick(() => {
        const tableBody = this.$refs.orgRiskTable?.$el?.querySelector(".el-table__body-wrapper");

        if (tableBody) {
          tableBody.scrollTop = 0;
        }
      });
      // 如果在省份视图，查询当前页机构的坐标
      if (this.mapViewLevel === "province") {
        this.geocodeCurrentPageOrgs();
      }
    },

    handleOrgKeywordFilterInput() {
      this.scheduleOrgFilterApply(500, false);
    },

    handleOrgFilterChange() {
      this.scheduleOrgFilterApply(120, true);
    },

    scheduleOrgFilterApply(delay = 0, showLoadingImmediately = true) {
      if (this.orgFilterTimer) {
        clearTimeout(this.orgFilterTimer);
      }
      if (this.orgFilterLoadingTimer) {
        clearTimeout(this.orgFilterLoadingTimer);
      }
      if (showLoadingImmediately) {
        this.orgRiskLoading = true;
      }
      this.orgFilterTimer = setTimeout(() => {
        this.orgRiskLoading = true;
        this.$nextTick(() => {
          this.orgFilterTimer = setTimeout(() => {
            this.applyOrgFilters();
          }, 50);
        });
      }, delay);
    },

    applyOrgFilters() {
      this.appliedOrgNameFilter = this.orgNameFilter;
      this.appliedOrgRiskStatusFilter = this.orgRiskStatusFilter;
      this.appliedOrgDomainScaleFilter = this.orgDomainScaleFilter;
      this.appliedOrgSortMode = this.orgSortMode;
      this.orgCurrentPage = 1;
      this.geocodedOrgs = [];

      this.$nextTick(() => {
        if (this.mapViewLevel === "province") {
          this.geocodeCurrentPageOrgs();
        }
        this.orgFilterLoadingTimer = setTimeout(() => {
          this.orgRiskLoading = false;
        }, 180);
      });
    },

    resetAppliedOrgFilters() {
      this.appliedOrgNameFilter = this.orgNameFilter;
      this.appliedOrgRiskStatusFilter = this.orgRiskStatusFilter;
      this.appliedOrgDomainScaleFilter = this.orgDomainScaleFilter;
      this.appliedOrgSortMode = this.orgSortMode;
    },

    async resetOrgTableFilters() {
      if (this.orgFilterTimer) {
        clearTimeout(this.orgFilterTimer);
      }
      if (this.orgFilterLoadingTimer) {
        clearTimeout(this.orgFilterLoadingTimer);
      }
      this.orgNameFilter = "";
      this.orgRiskStatusFilter = "all";
      this.orgDomainScaleFilter = "all";
      this.orgSortMode = "chineseRisk";
      this.orgCurrentPage = 1;
      this.orgRiskLoading = true;
      this.resetAppliedOrgFilters();
      if (this.orgProvinceFilter !== "all") {
        this.orgProvinceFilter = "all";
        await this.handleOrgProvinceFilterChange("all");
        return;
      }
      this.orgFilterLoadingTimer = setTimeout(() => {
        this.orgRiskLoading = false;
      }, 180);
    },

    async handleOrgProvinceFilterChange(provinceName) {
      if (this.orgFilterTimer) {
        clearTimeout(this.orgFilterTimer);
      }
      if (this.orgFilterLoadingTimer) {
        clearTimeout(this.orgFilterLoadingTimer);
      }
      this.orgCurrentPage = 1;
      this.geocodedOrgs = [];
      this.orgRiskLoading = true;
      this.resetAppliedOrgFilters();

      if (provinceName === "all") {
        if (this.mapViewLevel === "province") {
          this.backToChinaView({ reloadMap: true, reloadOrg: true });
          return;
        }
        await this.loadOrgRiskData();
        return;
      }

      this.provinceLoading = true;
      this.provinceLoadingProgress = 0;
      const progressTimer = setInterval(() => {
        if (this.provinceLoadingProgress < 85) {
          this.provinceLoadingProgress += Math.random() * 12 + 3;
          if (this.provinceLoadingProgress > 85) {
            this.provinceLoadingProgress = 85;
          }
        }
      }, 300);

      try {
        const res = await getDomainAuthRiskMapDetail({ 省份名称: provinceName });

        let orgList = [];

        if (res.code === 200 && res.data) {
          orgList = res.data.机构列表 || res.data || [];
        } else if (Array.isArray(res)) {
          orgList = res;
        } else if (res.机构列表) {
          orgList = res.机构列表;
        }

        this.selectedProvince = provinceName;
        this.provinceOrgList = orgList;
        this.mapViewLevel = "province";
        await this.updateMapForProvince(provinceName);
        this.updateOrgRiskDataForProvince(provinceName, orgList);
        this.provinceLoadingProgress = 100;
      } catch (error) {
        console.error("按省份过滤机构失败", error);
        this.$message.error("按省份过滤机构失败");
      } finally {
        clearInterval(progressTimer);
        setTimeout(() => {
          this.provinceLoading = false;
          this.provinceLoadingProgress = 0;
          this.orgRiskLoading = false;
        }, 400);
      }
    },

    toOrgRiskRow(row) {
      return {
        org: row.org || "-",
        domainCount: row.domainCount ?? "-",
        riskCount: row.riskCount ?? "-",
        hijackCount: row.hijackCount === "-" ? 0 : (row.hijackCount ?? "-")
      };
    },

    normalizeNumber(value) {
      if (value === "-" || value === undefined || value === null || value === "") {
        return 0;
      }
      const num = Number(value);

      return Number.isFinite(num) ? num : 0;
    },

    startsWithChinese(value) {
      return /^[\u4e00-\u9fff]/.test(String(value || "").trim());
    },

    sortOrgRows(rows) {
      return [...rows].sort((a, b) => {
        const aChinese = this.startsWithChinese(a.org);
        const bChinese = this.startsWithChinese(b.org);

        if (this.appliedOrgSortMode === "chineseRisk" && aChinese !== bChinese) {
          return aChinese ? -1 : 1;
        }

        if (this.appliedOrgSortMode === "nameAsc") {
          if (aChinese !== bChinese) {
            return aChinese ? -1 : 1;
          }
          return String(a.org || "").localeCompare(String(b.org || ""), "zh-Hans-CN");
        }

        if (this.appliedOrgSortMode === "domainDesc") {
          const domainDiff = this.normalizeNumber(b.domainCount) - this.normalizeNumber(a.domainCount);

          if (domainDiff !== 0) {
            return domainDiff;
          }
        }

        if (this.appliedOrgSortMode === "hijackDesc") {
          const hijackDiff = this.normalizeNumber(b.hijackCount) - this.normalizeNumber(a.hijackCount);

          if (hijackDiff !== 0) {
            return hijackDiff;
          }
        }

        const riskDiff = this.normalizeNumber(b.riskCount) - this.normalizeNumber(a.riskCount);

        if (riskDiff !== 0) {
          return riskDiff;
        }

        const domainDiff = this.normalizeNumber(b.domainCount) - this.normalizeNumber(a.domainCount);

        if (domainDiff !== 0) {
          return domainDiff;
        }

        return String(a.org || "").localeCompare(String(b.org || ""), "zh-Hans-CN");
      });
    },

    // 地理编码当前页的机构
    async geocodeCurrentPageOrgs() {
      if (this.geocodingInProgress) {
        return;
      }

      const currentPageOrgs = this.pagedOrgRiskData;

      if (!currentPageOrgs || currentPageOrgs.length === 0) {
        return;
      }

      this.geocodingInProgress = true;

      // 过滤出还没有地理编码的机构
      const orgsToGeocode = currentPageOrgs.filter(org =>
        !this.geocodedOrgs.find(g => g.name === org.org)
      );

      if (orgsToGeocode.length === 0) {
        this.geocodingInProgress = false;
        return;
      }

      // 限制每次最多查询10个
      const batchSize = 40;
      const batch = orgsToGeocode.slice(0, batchSize);

      try {
        // 串行查询，每次查询间隔100ms避免限流
        for (let i = 0; i < batch.length; i++) {
          const org = batch[i];

          // 如果页面已切换或返回全国视图，停止查询
          if (this.mapViewLevel !== "province") {
            console.log("[Geocode] 视图已切换，停止查询");
            break;
          }

          const result = await this.geocodeAddress(org.org);

          if (result) {
            const { lng, lat } = result;

            this.geocodedOrgs.push({
              name: org.org,
              lng: lng,
              lat: lat,
              ...org
            });
            // 每次成功后更新地图
            this.updateMapScatterPoints();
          }

          // 添加延迟，避免被限流（最后一个不需要等待）
          if (i < batch.length - 1) {
            await this.delay(500);
          }
        }
      } catch (error) {
        console.error("地理编码查询失败:", error);
      } finally {
        this.geocodingInProgress = false;
      }
    },

    // 延迟函数
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // 通过后端代理调用高德 /v3/place/text，按机构名查询坐标
    async geocodeAddress(address) {
      try {
        const params = new URLSearchParams({ keywords: address });

        if (this.selectedProvince) {
          params.set("city", this.selectedProvince);
        }
        const url = `/amap-geocode?${params.toString()}`;
        const resp = await axios.get(url, { timeout: 10000 });
        const data = resp.data || {};

        console.log(`[Geocode Debug] keyword="${address}" status=${data.status} count=${data.count}`);

        if (data.status === "1" && Array.isArray(data.pois) && data.pois.length > 0) {
          const poi = data.pois[0];
          const parts = (poi.location || "").split(",");
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);

          if (!isNaN(lng) && !isNaN(lat)) {
            console.log(`[Geocode] 成功: ${address} -> ${lng}, ${lat} (${poi.name})`);
            return { lng, lat };
          }
        }

        console.log(`[Geocode] 无结果: ${address} (status=${data.status} info=${data.info})`, data);
        return null;
      } catch (error) {
        console.error(`[Geocode] 查询失败: ${address}`, error);
        return null;
      }
    },

    // 更新地图上的散点标记
    updateMapScatterPoints() {
      if (!this.mapChart || this.geocodedOrgs.length === 0) {
        return;
      }

      const scatterData = this.geocodedOrgs.map(org => ({
        name: org.name,
        value: [org.lng, org.lat],
        orgData: org
      }));

      // 获取当前地图配置并添加散点系列
      const currentOption = this.mapChart.getOption();

      this.mapChart.setOption({
        series: [
          // 保留原有的地图系列（如果有）
          ...(currentOption.series || []).filter(s => s.type !== "scatter"),
          // 添加散点系列
          {
            name: "机构位置",
            type: "scatter",
            coordinateSystem: "geo",
            symbol: "pin",
            symbolSize: 40,
            label: {
              show: false
            },
            itemStyle: {
              color: "#FF6B6B",
              borderColor: "#fff",
              borderWidth: 2,
              shadowBlur: 10,
              shadowColor: "rgba(255, 107, 107, 0.5)"
            },
            emphasis: {
              itemStyle: {
                borderColor: "#FFD700",
                borderWidth: 3,
                shadowBlur: 15,
                shadowColor: "rgba(255, 215, 0, 0.8)"
              },
              label: {
                show: true,
                formatter: "{b}",
                position: "top",
                color: "#fff",
                fontSize: 12,
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                padding: [4, 8],
                borderRadius: 4
              }
            },
            data: scatterData
          }
        ],
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            if (params.seriesType === "scatter" && params.data.orgData) {
              const org = params.data.orgData;

              return `
                <div style="text-align: left;">
                  <strong>${org.name}</strong><br/>
                  域名数量: ${org.domainCount}<br/>
                  配置风险: ${org.riskCount}<br/>
                  劫持事件: ${org.hijackCount}
                </div>
              `;
            }
            return params.name;
          }
        }
      });

      // 添加散点点击事件
      this.bindScatterClickEvent();
    },

    // 绑定散点点击事件
    bindScatterClickEvent() {
      // 先移除之前的点击事件，避免重复绑定
      this.mapChart.off("click");
      this.mapChart.on("click", (params) => {
        if (params.seriesType === "scatter" && params.data.orgData) {
          // 点击散点，跳转到实体详情页
          this.$router.push({
            path: "/entitylink",
            query: { keyword: params.data.orgData.name }
          });
        }
      });
    },

    // 处理组织机构行点击
    handleOrgClick(row) {
      // 跳转到页面1，传递组织机构名称
      this.$router.push({
        path: "/entitylink",
        query: { keyword: row.org }
      });
    },

    // 导航切换
    handleNavChange(value) {
      if (value === "entitylink") {
        this.$router.push("/entitylink");
      } else if (value === "recrisk") {
        this.$router.push("/recrisk");
      }
    },

    setHijackTimeFilter(filter) {
      this.hijackTimeFilter = filter;
      this.$nextTick(() => {
        const tableBody = this.$refs.hijackEventTable?.$el?.querySelector(".el-table__body-wrapper");

        if (tableBody) {
          tableBody.scrollTop = 0;
        }
      });
    },

    getHijackTimeCutoff(filter) {
      const DAY_MS = 24 * 60 * 60 * 1000;
      const now = Date.now();

      if (filter === "3m") {
        return new Date(now - 90 * DAY_MS);
      }
      if (filter === "1m") {
        return new Date(now - 30 * DAY_MS);
      }
      if (filter === "1w") {
        return new Date(now - 7 * DAY_MS);
      }
      return null;
    },

    countHijackEventsByPeriod(filter) {
      const cutoff = this.getHijackTimeCutoff(filter);

      if (!cutoff) {
        return this.hijackEventData.filter(item => item.timeValue).length;
      }
      return this.hijackEventData.filter(item => item.timeValue && item.timeValue >= cutoff.getTime()).length;
    },

    parseHijackTime(value) {
      if (!value) {
        return null;
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      if (typeof value === "number") {
        const date = new Date(value);

        return Number.isNaN(date.getTime()) ? null : date;
      }
      const text = String(value).trim();
      const compactHourMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})-(\d{1,2})$/);

      if (compactHourMatch) {
        const [, year, month, day, hour] = compactHourMatch;
        const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour));

        return Number.isNaN(date.getTime()) ? null : date;
      }
      const compactDateTimeMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?/);

      if (compactDateTimeMatch) {
        const [, year, month, day, hour = "0", minute = "0", second = "0"] = compactDateTimeMatch;
        const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));

        return Number.isNaN(date.getTime()) ? null : date;
      }
      const chineseMatch = text.match(/(\d{4})年(\d{1,2})月(\d{1,2})日\s*(\d{1,2})?时?(\d{1,2})?分?(\d{1,2})?秒?/);

      if (chineseMatch) {
        const [, year, month, day, hour = "0", minute = "0", second = "0"] = chineseMatch;
        const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));

        return Number.isNaN(date.getTime()) ? null : date;
      }
      const normalized = text
        .replace(/年|月/g, "-")
        .replace(/日/g, " ")
        .replace(/时/g, ":00:00")
        .replace(/分/g, ":00")
        .replace(/\//g, "-");
      const date = new Date(normalized);

      return Number.isNaN(date.getTime()) ? null : date;
    },

    formatHijackTime(date, fallback) {
      if (!date) {
        return fallback || "-";
      }
      const pad = num => String(num).padStart(2, "0");

      return `${date.getFullYear()}年${pad(date.getMonth() + 1)}月${pad(date.getDate())}日${pad(date.getHours())}时`;
    },

    // 截断劫持目的文本，生成简短摘要
    formatIPs(ipStr) {
      if (!ipStr || ipStr === "-") {
        return "-";
      }
      // 如果是数组，先展开为字符串
      const str = Array.isArray(ipStr) ? ipStr.join(",") : String(ipStr);
      // 用正则提取所有IPv4地址（处理多个IP连在一起的情况）
      const ips = str.match(/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/g);

      return ips ? ips.join("\n") : str;
    },

    truncatePurpose(text) {
      if (!text || text === "-") {
        return "-";
      }
      // 限制最大长度为50个字符
      const maxLength = 50;

      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + "...";
    },

    formatConfidence(value) {
      if (value === undefined || value === null || value === "") {
        return "-";
      }
      if (value === "-") {
        return "-";
      }
      const text = String(value).trim();
      const hasPercent = text.endsWith("%");
      const num = Number(hasPercent ? text.slice(0, -1) : text);

      if (!Number.isFinite(num)) {
        return text;
      }
      const formatted = Number(num.toFixed(2)).toString();

      return hasPercent ? `${formatted}%` : formatted;
    },

    getConfidenceMeta(value) {
      const label = this.formatConfidence(value);
      const raw = String(value ?? "").trim();
      const hasPercent = raw.endsWith("%");
      const parsed = Number(hasPercent ? raw.slice(0, -1) : raw);

      if (!Number.isFinite(parsed)) {
        return {
          label,
          value: null,
          width: "0%",
          className: "unknown"
        };
      }
      const percent = Math.max(0, Math.min(100, hasPercent || parsed > 1 ? parsed : parsed * 100));

      let className = "low";

      if (percent >= 80) {
        className = "high";
      } else if (percent >= 50) {
        className = "medium";
      }
      return {
        label,
        value: percent,
        width: `${percent}%`,
        className
      };
    },

    getPurposeItems(text) {
      if (!text || text === "-") {
        return ["暂无分析"];
      }
      const cleaned = String(text)
        .replace(/劫持目的[:：]?/g, "")
        .replace(/劫持行为[:：]?/g, "")
        .replace(/智能分析[:：]?/g, "")
        .trim();
      const parts = cleaned
        .split(/[\n\r。；;]+/)
        .map(item => item.replace(/^[\s\d、.．\-•]+/, "").trim())
        .filter(Boolean);
      const source = parts.length ? parts : [cleaned];

      return source.slice(0, 3).map(item => {
        const maxLength = 34;

        return item.length > maxLength ? `${item.slice(0, maxLength)}...` : item;
      });
    },

    // ============ 机构搜索相关方法 ============

    isEmptyBackendData(data) {
      if (!data) {
        return true;
      }
      if (Array.isArray(data)) {
        return data.length === 0;
      }
      if (typeof data !== "object") {
        return false;
      }
      return Object.values(data).every(value => {
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return value === undefined || value === null || value === "";
      });
    },

    hasDomainListData(data) {
      return Boolean(data && Array.isArray(data.域名) && data.域名.length > 0);
    },

    // 处理机构搜索
    async handleOrgSearch() {
      if (!this.orgSearchKeyword.trim()) {
        this.$message.warning("请输入机构名称");
        return;
      }
      await this.doOrgSearch();
    },

    async doOrgSearch() {
      this.orgDomainCurrentPage = 1;
      this.isOrgLoading = true;
      this.orgLoadingStepIndex = 0;
      this.orgLoadingText = this.orgLoadingSteps[0];
      this.orgLoadingTimer = setInterval(() => {
        this.orgLoadingStepIndex = (this.orgLoadingStepIndex + 1) % this.orgLoadingSteps.length;
        this.orgLoadingText = this.orgLoadingSteps[this.orgLoadingStepIndex];
      }, 800);

      try {
        const keyword = this.orgSearchKeyword.trim();

        this.searchedEntityInfo = { 实体规范名称: "", 二级域名数量: 0, 子域名数量: 0 };
        this.orgRiskStatsData = [];
        this.orgCompanyRiskRows = [];
        this.orgDomainListData = [];
        this.orgDomainTotal = 0;

        // 并发请求三个接口
        const [entityInfoRes, riskStatsRes, domainListRes] = await Promise.all([
          getEntityInfo({ 实体名或关键词: keyword }),
          getDomainAuthRisk({ 实体名或关键词: keyword }),
          getDomainAuthRiskList({ 实体名或关键词: keyword })
        ]);

        if (
          !(entityInfoRes.code === 200)
          || this.isEmptyBackendData(entityInfoRes.data)
          || !(domainListRes.code === 200)
          || !this.hasDomainListData(domainListRes.data)
        ) {
          this.$message.warning("该机构不在监测范围内");
          return;
        }

        const entityInfo = entityInfoRes.data;
        const standardName = entityInfo.实体规范名称 || keyword;

        this.searchedEntityInfo = entityInfo;
        this.orgSearchKeyword = standardName;
        this.searchedOrgName = standardName;

        if (riskStatsRes.code === 200 && riskStatsRes.data) {
          const data = riskStatsRes.data;

          this.orgRiskStatsData = (data.风险来源 || []).map((source, index) => ({
            riskSource: source,
            riskLevel: this.formatRiskLevelLabel(data.风险等级[index]),
            domainCount: data.二级域名数量[index],
            subdomainCount: data.子域名数量[index]
          }));
        }

        if (domainListRes.code === 200 && domainListRes.data) {
          const data = domainListRes.data;

          this.orgDomainListData = (data.域名 || []).map((domain, index) => ({
            domain: domain,
            severe: data.严重配置风险警告[index],
            important: data.重要配置风险告警[index],
            general: data.一般配置风险提示[index],
            hijack: data.历史域名劫持事件[index],
            companies: null
          }));
          this.orgDomainTotal = this.orgDomainListData.length;

          // 批量查询所有域名的归属机构，超过1000个时分块并发
          const allDomains = this.orgDomainListData.map(r => r.domain);
          const chunkSize = 1000;
          const chunks = [];

          for (let i = 0; i < allDomains.length; i += chunkSize) {
            chunks.push(allDomains.slice(i, i + chunkSize));
          }
          Promise.all(chunks.map(chunk => getDomainCompanyBatch(chunk))).then(responses => {
            const merged = {};

            responses.forEach(res => {
              if (res && res.result) {
                Object.assign(merged, res.result);
              }
            });
            const companyFreq = new Map();

            this.orgDomainListData.forEach((row, idx) => {
              const key = row.domain.toLowerCase().replace(/\.$/, "");
              const companies = merged[key] || [];

              this.$set(this.orgDomainListData[idx], "companies", companies);
              companies.forEach(c => {
                if (c) {
                  companyFreq.set(c, (companyFreq.get(c) || 0) + 1);
                }
              });
            });
            this.orgDomainListData = this.groupRowsByCompanies(this.orgDomainListData);
            this.orgCompanyRiskRows = this.buildCompanyRiskRows(this.orgDomainListData, standardName);
            this.orgDomainCurrentPage = 1;
            const sortedCompanies = [...companyFreq.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([c]) => c);

            this.showOrgsOnMap(sortedCompanies, standardName);
          }).catch(() => {
            this.orgDomainListData.forEach((_, idx) => {
              this.$set(this.orgDomainListData[idx], "companies", []);
            });
            this.orgCompanyRiskRows = this.buildCompanyRiskRows(this.orgDomainListData, standardName);
            this.showOrgsOnMap([], standardName);
          });
        }

        this.hasOrgSearched = true;
      } catch (error) {
        console.error("机构搜索失败", error);
        this.$message.error("查询失败，请稍后重试");
      } finally {
        clearInterval(this.orgLoadingTimer);
        this.orgLoadingTimer = null;
        this.isOrgLoading = false;
      }
    },

    // 在地图上显示机构位置（按搜索域名归属的全部原始机构定位）
    async showOrgsOnMap(companies, fallbackName) {
      if (!this.mapChart) {
        return;
      }

      // 先重置地图为全国视图
      echarts.registerMap("china", chinaMapData);
      this.mapChart.setOption({
        geo: {
          map: "china",
          center: null,
          zoom: 1,
          roam: true,
          ...this.getChinaMapLayout(),
          itemStyle: {
            areaColor: "#1e3a8a",
            borderColor: "#4da6ff",
            borderWidth: 1
          },
          emphasis: {
            itemStyle: { areaColor: "#40a9ff", borderColor: "#fff", borderWidth: 2 },
            label: { show: true, color: "#fff" }
          },
          label: { show: false }
        },
        series: [],
        visualMap: { show: false }
      }, true);

      const savedProvince = this.selectedProvince;

      this.selectedProvince = null; // 使用全国范围
      try {
        // 限制最多 geocode 30 个机构，防止 keyword 太多被限流
        const targets = (companies || []).slice(0, 30);

        if (!targets.length) {
          await this.locateSingleOrgOnMap(fallbackName);
          return;
        }

        const points = [];

        for (let i = 0; i < targets.length; i++) {
          // 用户已退出搜索模式，停止后续 geocode
          if (!this.hasOrgSearched) {
            break;
          }
          const name = targets[i];
          const result = await this.geocodeAddress(name);

          if (result) {
            points.push({ name, lng: result.lng, lat: result.lat });
            this.renderOrgScatter(points, points.length === 1 ? points[0] : null);
          }
          if (i < targets.length - 1) {
            await this.delay(400);
          }
        }

        if (!points.length) {
          // 全部 geocode 失败，回退到规范名称
          await this.locateSingleOrgOnMap(fallbackName);
          return;
        }

        if (points.length === 1) {
          // 单点：放大定位
          this.renderOrgScatter(points, points[0]);
        } else {
          // 多点：保持全国视图，展示所有 pin
          this.renderOrgScatter(points, null);
        }
      } finally {
        this.selectedProvince = savedProvince;
      }
    },

    async locateSingleOrgOnMap(orgName) {
      if (!orgName) {
        this.$message.warning("未能找到该机构的地理位置，已显示全国地图");
        return;
      }
      const result = await this.geocodeAddress(orgName);

      if (!result) {
        this.$message.warning("未能找到该机构的地理位置，已显示全国地图");
        return;
      }
      this.renderOrgScatter([{ name: orgName, lng: result.lng, lat: result.lat }], { name: orgName, lng: result.lng, lat: result.lat });
    },

    renderOrgScatter(points, focus) {
      if (!this.mapChart || !points.length) {
        return;
      }
      const scatterData = points.map(p => ({ name: p.name, value: [p.lng, p.lat] }));
      const option = {
        series: [
          {
            name: "机构位置",
            type: "scatter",
            coordinateSystem: "geo",
            symbol: "pin",
            symbolSize: 40,
            label: {
              show: points.length === 1,
              formatter: "{b}",
              position: "top",
              color: "#fff",
              fontSize: 12,
              backgroundColor: "rgba(0,0,0,0.6)",
              padding: [4, 8],
              borderRadius: 4
            },
            itemStyle: {
              color: "#FF6B6B",
              borderColor: "#FFD700",
              borderWidth: 2,
              shadowBlur: 12,
              shadowColor: "rgba(255, 107, 107, 0.8)"
            },
            emphasis: {
              label: {
                show: true,
                formatter: "{b}",
                position: "top",
                color: "#fff",
                fontSize: 12,
                backgroundColor: "rgba(0,0,0,0.6)",
                padding: [4, 8],
                borderRadius: 4
              }
            },
            data: scatterData
          }
        ],
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            if (params.seriesType === "scatter") {
              const [lng, lat] = params.data.value;

              return `<strong>${params.name}</strong><br/>经度: ${lng.toFixed(4)}<br/>纬度: ${lat.toFixed(4)}`;
            }
            return params.name;
          }
        }
      };

      if (focus) {
        option.geo = { center: [focus.lng, focus.lat], zoom: 8 };
      } else {
        option.geo = { center: null, zoom: 1, ...this.getChinaMapLayout() };
      }
      this.mapChart.setOption(option);
    },

    // 返回总览
    backToOverview() {
      this.hasOrgSearched = false;
      this.orgSearchKeyword = "";
      this.searchedOrgName = "";
      this.searchedEntityInfo = { 实体规范名称: "", 二级域名数量: 0, 子域名数量: 0 };
      this.orgRiskStatsData = [];
      this.orgCompanyRiskRows = [];
      this.orgDomainListData = [];
      this.orgDomainTotal = 0;
      this.orgDomainCurrentPage = 1;
      this.orgDialogVisible = false;
      this.orgDomainDetail = null;

      // 恢复全国地图
      this.backToChinaView();
    },

    // 搜索框清空时返回总览
    onOrgSearchClear() {
      if (this.hasOrgSearched) {
        this.backToOverview();
      }
    },

    // 处理域名行点击（弹出详情）
    async handleOrgDomainClick(row) {
      this.orgSelectedDomain = row.domain;
      this.orgDialogVisible = true;
      this.orgDomainDetail = null;

      try {
        const res = await getDomainAuthRiskDetail({ 域名: row.domain });

        if (res.code === 200) {
          const detail = res.data;

          if (detail && Array.isArray(detail.域名劫持事件)) {
            detail.域名劫持事件 = detail.域名劫持事件
              .slice()
              .sort((a, b) => (b.发生时间 > a.发生时间 ? 1 : b.发生时间 < a.发生时间 ? -1 : 0));
          }
          this.orgDomainDetail = detail;
        }
      } catch (error) {
        console.error("获取域名详情失败", error);
        this.$message.error("获取域名详情失败");
      }
    },

    // 机构域名列表分页
    handleOrgDomainPageChange(page) {
      this.orgDomainCurrentPage = page;
    },

    // 风险等级样式类
    getRiskClass(riskLevel) {
      const classMap = {
        "域名劫持事件": "risk-tag-hijack",
        "严重配置风险警告": "risk-tag-severe",
        "重要配置风险告警": "risk-tag-important",
        "一般配置风险提示": "risk-tag-general",
        "一般配置提示": "risk-tag-general"
      };

      return classMap[riskLevel] || "";
    },

    formatRiskLevelLabel(riskLevel) {
      return riskLevel === "一般配置风险提示" ? "一般配置提示" : riskLevel;
    },

    buildCompanyRiskRows(rows, fallbackName) {
      const companyStats = new Map();

      rows.forEach(row => {
        const companies = Array.isArray(row.companies) && row.companies.length
          ? row.companies
          : [fallbackName || "未识别归属机构"];
        const riskCount = this.normalizeNumber(row.severe) + this.normalizeNumber(row.important) + this.normalizeNumber(row.general);
        const hijackCount = this.normalizeNumber(row.hijack);

        companies.forEach(company => {
          const name = company || "未识别归属机构";
          const stat = companyStats.get(name) || {
            org: name,
            domainCount: 0,
            riskCount: 0,
            hijackCount: 0
          };

          stat.domainCount += 1;
          stat.riskCount += riskCount;
          stat.hijackCount += hijackCount;
          companyStats.set(name, stat);
        });
      });

      return [...companyStats.values()].sort((a, b) => {
        const riskDiff = b.riskCount - a.riskCount;

        if (riskDiff !== 0) {
          return riskDiff;
        }
        const domainDiff = b.domainCount - a.domainCount;

        if (domainDiff !== 0) {
          return domainDiff;
        }
        return String(a.org || "").localeCompare(String(b.org || ""), "zh-Hans-CN");
      });
    },

    // 把同机构的域名相邻摆放：以 companies 排序后 join 作为分组 key，
    // 按各分组首次出现顺序输出；无机构归属的行集中排到最后
    groupRowsByCompanies(rows) {
      const order = new Map();
      const buckets = new Map();
      const empty = [];

      rows.forEach(row => {
        const list = Array.isArray(row.companies) ? row.companies : [];

        if (!list.length) {
          empty.push(row);
          return;
        }
        const key = [...list].sort().join("||");

        if (!order.has(key)) {
          order.set(key, order.size);
          buckets.set(key, []);
        }
        buckets.get(key).push(row);
      });

      const ordered = [];

      [...order.keys()].forEach(key => {
        ordered.push(...buckets.get(key));
      });
      return ordered.concat(empty);
    }
  }
};
</script>

<style lang="less" scoped>
.auth-risk-container {
  width: 100%;
  min-height: var(--screen-height, 100vh);
  height: var(--screen-height, 100vh);
  background: url("../assets/background.png") no-repeat center center #000;
  background-size: cover;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
}

.leftSider, .rightSider {
  position: fixed;
  z-index: 1;
  display: none;

  @media (min-width: 1366px) {
    display: block;
  }

  img {
    height: var(--screen-height, 100vh);
    width: auto;
  }
}

.leftSider {
  left: 0;
  top: 0;
}

.rightSider {
  right: 0;
  top: 0;
}

.main-content {
  position: absolute;
  top: 180px;
  left: 0;
  right: 0;
  bottom: 80px;
  padding: 0 2% 80px;
  z-index: 10;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  overflow-x: hidden;
  box-sizing: border-box;
  width: 100%; // 改用100%而不是100vw

  // 移动端和小屏：单列布局
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 768px) {
    top: 200px;
    padding: 0 4% 80px;
    gap: 20px;
  }

  // 大屏：网格布局（只在足够宽的屏幕上使用grid）
  @media (min-width: 1280px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1fr); // 使用minmax确保严格比例
    grid-template-rows: minmax(calc(var(--screen-height, 100vh) - 700px), 1fr) auto auto;
    grid-template-areas:
      "left center right"
      "bottom bottom bottom"
      "beian beian beian";
    gap: 20px;
  }

  &.org-search-content {
    bottom: auto;
    min-height: calc(var(--screen-height, 100vh) - 260px);
    padding-bottom: 120px;
  }

  @media (min-width: 1280px) {
    &.org-search-content {
      grid-template-rows: minmax(400px, 1fr) minmax(0, auto);
    }
  }

  @media (min-width: 1366px) {
    padding: 0 5% 80px; // 使用百分比保持一致性，避免布局重算
  }
}

// 移动端布局调整
.left-section {
  min-width: 0; // 防止内容撑大容器
  max-width: 100%; // 限制最大宽度
  overflow: hidden; // 隐藏溢出内容

  @media (min-width: 1280px) {
    grid-area: left;
  }
}

.center-section {
  min-height: 400px;
  min-width: 0; // 防止内容撑大容器
  max-width: 100%; // 限制最大宽度
  overflow: hidden; // 隐藏溢出内容

  .card-title {
    min-height: 40px;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  @media (min-width: 1280px) {
    grid-area: center;
  }
}

.right-section {
  min-width: 0; // 防止内容撑大容器
  max-width: 100%; // 限制最大宽度
  overflow: hidden; // 隐藏溢出内容

  @media (min-width: 1280px) {
    grid-area: right;
  }
}

.bottom-section {
  min-width: 0; // 防止内容撑大容器

  @media (min-width: 1280px) {
    grid-area: bottom;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: clamp(15px, 2vw, 20px);
  height: 100%;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.card-title {
  font-size: clamp(14px, 1.8vw, 18px);
  color: #fff;
  font-weight: bold;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #1890FF;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title-main {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .scope-tip-icon {
    color: #7ed6ff;
    cursor: help;
    flex: 0 0 auto;
    font-size: 15px;
    line-height: 1;
    opacity: 0.88;
    transition: color 0.2s ease, opacity 0.2s ease;

    &:hover {
      color: #40a9ff;
      opacity: 1;
    }
  }

  .back-button {
    margin-left: auto;
    background: rgba(24, 144, 255, 0.8);
    border-color: #1890FF;
    color: #fff;
    padding: 5px 15px;
    font-size: 12px;

    &:hover {
      background: #1890FF;
      border-color: #40a9ff;
    }
  }

  .subtitle {
    font-size: clamp(12px, 1.5vw, 14px);
    font-weight: normal;
    color: rgba(255, 255, 255, 0.65);
    margin-left: 10px;
    display: inline-block;

    @media (max-width: 768px) {
      display: block;
      margin-left: 0;
      margin-top: 5px;
    }

    .highlight {
      color: #1890FF;
      font-weight: bold;
      font-size: clamp(14px, 1.7vw, 16px);
      margin: 0 3px;
    }
  }
}

.map-loading-bar {
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;

  .map-loading-progress {
    height: 100%;
    background: linear-gradient(90deg, #1890FF, #40a9ff);
    border-radius: 2px;
    transition: width 0.3s ease;
  }
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 350px;

  @media (min-width: 768px) {
    min-height: 400px;
  }
}

.chart-container {
  width: 100%;
  height: 400px;
  min-height: 300px;

  @media (min-width: 768px) {
    height: 450px;
  }
}

// 统计卡片容器
.stats-cards-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  // 自定义滚动条
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(24, 144, 255, 0.3);
    border-radius: 3px;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(24, 144, 255, 0.5);
    }
  }

  // 防止滚动条区域触发卡片hover效果
  &::-webkit-scrollbar-button {
    display: none;
  }
}

// 统计卡片项
.stats-card-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  min-height: 90px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    transition: all 0.3s ease;
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  // 严重风险 - 橙色
  &.risk-level-critical {
    &::before {
      background: #FA8C16;
    }
    &:hover {
      border-color: rgba(250, 140, 22, 0.5);
      background: rgba(250, 140, 22, 0.08);
    }
  }

  // 重要风险 - 黄色
  &.risk-level-high {
    &::before {
      background: #FAAD14;
    }
    &:hover {
      border-color: rgba(250, 173, 20, 0.5);
      background: rgba(250, 173, 20, 0.08);
    }
  }

  // 一般提示 - 蓝色
  &.risk-level-medium {
    &::before {
      background: #1890FF;
    }
    &:hover {
      border-color: rgba(24, 144, 255, 0.5);
      background: rgba(24, 144, 255, 0.08);
    }
  }
}

// 卡片头部
.stats-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;

  .risk-icon {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;

    &.icon-critical {
      background: rgba(250, 140, 22, 0.2);
      color: #FA8C16;
    }

    &.icon-high {
      background: rgba(250, 173, 20, 0.2);
      color: #FAAD14;
    }

    &.icon-medium {
      background: rgba(24, 144, 255, 0.2);
      color: #1890FF;
    }
  }

  .risk-name {
    font-size: 16px;
    color: #fff;
    font-weight: 600;
    line-height: 1.3;
    cursor: help;
    transition: color 0.3s ease;

    &:hover {
      color: #1890ff;
    }
  }
}

// 卡片主体
.stats-card-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;

  .risk-count-wrapper {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .risk-count {
    font-size: 36px;
    font-weight: bold;
    color: #fff;
    line-height: 1;
  }

  .risk-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
  }

  .risk-level-badge {
    padding: 5px 14px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .risk-level-critical & .risk-level-badge {
    background: rgba(250, 140, 22, 0.2);
    color: #FA8C16;
    border: 1px solid rgba(250, 140, 22, 0.3);
  }

  .risk-level-high & .risk-level-badge {
    background: rgba(250, 173, 20, 0.2);
    color: #FAAD14;
    border: 1px solid rgba(250, 173, 20, 0.3);
  }

  .risk-level-medium & .risk-level-badge {
    background: rgba(24, 144, 255, 0.2);
    color: #1890FF;
    border: 1px solid rgba(24, 144, 255, 0.3);
  }
}

// 表格样式
/deep/ .el-table {
  background: transparent;
  color: #fff;
  width: 100% !important;
  table-layout: fixed !important; // 固定布局，强制遵循容器宽度

  th {
    background: rgba(24, 144, 255, 0.1);
    color: #fff;
    font-weight: bold;
    border-color: rgba(255, 255, 255, 0.1);
  }

  td {
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
  }

  tr {
    background: transparent;

    &:hover > td {
      background: rgba(24, 144, 255, 0.1) !important;
      cursor: pointer;
    }
  }

  &::before {
    display: none;
  }

  // 强制表头和表体遵循容器宽度
  .el-table__header-wrapper,
  .el-table__body-wrapper {
    width: 100% !important;
  }

  .el-table__header,
  .el-table__body {
    width: 100% !important;
  }

  // 确保单元格内容正常显示，不强制省略
  .cell {
    white-space: normal; // 允许换行
    word-break: break-word; // 允许单词内换行
    overflow: visible; // 显示所有内容
    text-overflow: clip; // 不使用省略号
  }
}

.org-table-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: -4px 0 10px;

  /deep/ .el-select {
    width: 126px;
    flex: 0 0 auto;
  }

  > .el-input {
    width: 132px;
    flex: 1 1 132px;
    min-width: 112px;
  }

  > .el-input.org-keyword-input {
    flex: 1 0 100%;
    width: 100%;
    min-width: 0;
  }

  /deep/ .el-input__inner {
    height: 28px;
    line-height: 28px;
    background: rgba(12, 28, 66, 0.72);
    border-color: rgba(126, 214, 255, 0.35);
    color: #fff;
  }

  .el-button {
    height: 28px;
    padding: 0 8px;
    color: #9fd8ff;
    border-color: rgba(126, 214, 255, 0.35);
    background: rgba(12, 28, 66, 0.58);
  }
}

.org-risk-table {
  flex: 1;
  height: 100%; // 占满父容器高度

  /deep/ .el-table__body-wrapper {
    overflow-y: auto !important; // 使用auto，有内容时才显示滚动条
    overflow-x: hidden !important;
    max-height: 350px !important; // 减小高度，让滚动条滑块变短

    // 自定义滚动条样式（与stats-cards-container保持一致）
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(24, 144, 255, 0.3);
      border-radius: 3px;
      transition: background 0.3s ease;

      &:hover {
        background: rgba(24, 144, 255, 0.5);
      }
    }

    // 防止滚动条区域触发行hover效果
    &::-webkit-scrollbar-button {
      display: none;
    }
  }
}

// 组织机构分页样式
.org-pagination {
  margin-top: 10px;
  text-align: center;

  /deep/ .el-pager li {
    background: transparent;
    color: rgba(255, 255, 255, 0.65);
    border: 1px solid rgba(255, 255, 255, 0.2);
    min-width: 28px;

    &.active {
      background: #1890FF;
      border-color: #1890FF;
      color: #fff;
    }

    &:hover {
      color: #1890FF;
    }
  }

  /deep/ .btn-prev,
  /deep/ .btn-next {
    background: transparent;
    color: rgba(255, 255, 255, 0.65);

    &:hover {
      color: #1890FF;
    }

    &:disabled {
      color: rgba(255, 255, 255, 0.3);
    }
  }
}

.hijack-event-table {
  flex: 1;
  height: 100%;

  /deep/ .el-table__body-wrapper {
    max-height: 250px;
    overflow-y: auto;
    overflow-x: auto;

    // 自定义滚动条样式
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(24, 144, 255, 0.3);
      border-radius: 3px;

      &:hover {
        background: rgba(24, 144, 255, 0.5);
      }
    }
  }
}

// 底部栏目特殊样式
.bottom-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.bottom-content {
  flex: 1;
  display: flex;
  gap: 20px;
  min-height: 0; // 重要：允许flex子项收缩

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

// 左侧统计面板
.stats-panel {
  flex: 0 0 450px; // 固定宽度450px
  background: linear-gradient(135deg, rgba(24, 144, 255, 0.1), rgba(0, 212, 255, 0.05));
  border: 1px solid rgba(24, 144, 255, 0.3);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column; // 纵向：标题在上，统计项在下
  gap: 15px;

  @media (max-width: 1280px) {
    flex: 0 0 auto; // 中等屏幕自适应
  }

  @media (max-width: 768px) {
    flex: 1 0 auto; // 小屏占满
  }

  .stats-title {
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    text-align: center;
    padding-bottom: 15px;
    border-bottom: 2px solid rgba(24, 144, 255, 0.5);
  }

  .stats-items-wrapper {
    display: flex;
    flex-direction: row;
    gap: 10px;
    flex: 1;
    justify-content: center;
  }

  .stats-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1 1 0;
    min-width: 0;
    min-height: 92px;
    padding: 14px 8px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(24, 144, 255, 0.15);
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
    }

    .stats-label {
      font-size: 20px;
      color: rgba(255, 255, 255, 0.7);
      white-space: nowrap;
      margin-bottom: 16px;
    }

    .stats-value {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 10px;
      max-width: 100%;

      .number {
        font-size: 28px;
        font-weight: bold;
        color: #1890FF;
        line-height: 1;
      }

      .unit {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.65);
      }
    }
  }
}

// 右侧事件面板
.events-panel {
  flex: 1;
  min-width: 0; // 重要：允许flex子项收缩
  display: flex;
  flex-direction: column;
}

.hijack-time-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: 12px;

  /deep/ .el-button {
    padding: 5px 10px;
    color: #bfe5ff;
    border-color: rgba(126, 214, 255, 0.38);
    background: rgba(12, 28, 66, 0.58);
  }

  /deep/ .el-button--primary {
    color: #fff;
    border-color: #1890FF;
    background: rgba(24, 144, 255, 0.82);
  }
}

.confidence-bar {
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);

  .confidence-fill {
    display: block;
    height: 100%;
    min-width: 4px;
    border-radius: inherit;
    transition: width 0.25s ease;

    &.high {
      background: linear-gradient(90deg, #52c41a, #95de64);
    }

    &.medium {
      background: linear-gradient(90deg, #faad14, #ffd666);
    }

    &.low {
      background: linear-gradient(90deg, #ff4d4f, #ff7875);
    }

    &.unknown {
      min-width: 0;
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.purpose-list {
  cursor: pointer;
  margin: 0;
  padding: 0 0 0 16px;
  max-width: 100%;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.45;

  li {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ============ 机构搜索相关样式 ============

.page-update-time {
  position: absolute;
  top: 118px;
  right: 5%;
  z-index: 12;
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
  line-height: 1;
  padding: 8px 12px;
  border: 1px solid rgba(126, 214, 255, 0.32);
  border-radius: 6px;
  background: rgba(7, 18, 49, 0.58);
  box-shadow: 0 6px 18px rgba(0, 18, 56, 0.2);
}

// header 内机构搜索框
.org-search-panel {
  width: min(760px, calc(100% - 64px));
  margin: 14px auto 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 18px;
  border: 1px solid rgba(126, 214, 255, 0.36);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(17, 32, 78, 0.92), rgba(25, 59, 111, 0.74));
  box-shadow: 0 10px 28px rgba(0, 18, 56, 0.26), inset 0 0 20px rgba(61, 173, 255, 0.08);

  /deep/ .el-input {
    flex: 1;
  }

  /deep/ .el-input__inner {
    background-color: rgba(7, 18, 49, 0.72);
    border: 1px solid rgba(126, 214, 255, 0.48);
    border-right: none;
    color: #fff;
    font-size: 14px;
    height: 38px;
    &::placeholder { color: rgba(255, 255, 255, 0.56); }
  }

  /deep/ .el-input-group__append {
    background: linear-gradient(135deg, rgba(21, 126, 251, 0.92), rgba(35, 198, 255, 0.84));
    border: 1px solid rgba(126, 214, 255, 0.55);
    border-left: none;
    color: #fff;
    padding: 0 14px;
  }

  /deep/ .el-input__suffix {
    right: 54px;
  }

  .back-overview-btn {
    flex: 0 0 auto;
    font-size: 13px;
    height: 36px;
    padding: 0 14px;
    background: rgba(250, 140, 22, 0.9);
    border-color: #fa8c16;
    color: #fff;

    &:hover {
      background: #fa8c16;
      border-color: #ffa940;
    }
  }
}

@media (max-width: 768px) {
  .page-update-time {
    top: 106px;
    right: 14px;
    font-size: 12px;
    padding: 6px 8px;
  }

  .org-search-panel {
    width: calc(100% - 28px);
    flex-direction: column;
    align-items: stretch;
    padding: 10px;
    margin-top: 10px;

    .back-overview-btn {
      width: 100%;
    }
  }
}

// 左侧机构信息卡（搜索模式）
// 右侧风险统计表格（搜索模式）
.org-stats-table {
  /deep/ .el-table__body-wrapper {
    overflow-y: auto;
    max-height: 350px;
  }
}

// 风险等级标签
.risk-tag-severe   { color: #FA8C16; font-weight: 600; }
.risk-tag-important { color: #FAAD14; font-weight: 600; }
.risk-tag-general  { color: #1890FF; font-weight: 600; }
.risk-tag-hijack   { color: #F5222D; font-weight: 600; }

// 底部域名资产列表表格
.org-domain-table {
  flex: 0 0 auto; // 不扩展，让分页按钮有空间显示

  /deep/ .clickable-row {
    cursor: pointer;
  }
}

// 机构搜索模式下底部卡片需留出分页空间
.bottom-card.org-search-bottom {
  height: auto; // 不强制撑满，让内容自然决定高度
  overflow: visible;
  padding-bottom: 10px;

  .org-pagination {
    margin-top: 10px;
    flex-shrink: 0; // 分页不被压缩
  }
}

// 风险计数标签（底部表格）
.risk-count-tag {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;

  &.severe   { color: #FA8C16; background: rgba(250, 140, 22, 0.1); }
  &.important { color: #FAAD14; background: rgba(250, 173, 20, 0.1); }
  &.general  { color: #1890FF; background: rgba(24, 144, 255, 0.1); }
  &.hijack   { color: #F5222D; background: rgba(245, 34, 45, 0.1); }
}

// 归属机构样式
.company-loading {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.company-none {
  color: rgba(255, 255, 255, 0.3);
}

.company-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.company-tag {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  max-width: none;
  white-space: normal;
  height: auto;
  line-height: 1.4;
  padding: 2px 6px;
}

// loading 遮罩（复用 EntityLink.vue 风格）
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .loading-dots {
    display: flex;
    gap: 10px;

    span {
      width: 12px;
      height: 12px;
      background: #1890FF;
      border-radius: 50%;
      animation: loading-bounce 1.4s infinite ease-in-out both;

      &:nth-child(1) { animation-delay: -0.32s; }
      &:nth-child(2) { animation-delay: -0.16s; }
      &:nth-child(3) { animation-delay: 0s; }
    }
  }

  .loading-text {
    color: #fff;
    font-size: 14px;
    opacity: 0.85;
    font-family: monospace;
    letter-spacing: 0.05em;
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

// 域名风险详情弹窗
.risk-detail-dialog {
  z-index: 1200;

  /deep/ .el-dialog {
    background: #0d1b3e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
  }

  /deep/ .el-dialog__title { color: #fff; }
  /deep/ .el-dialog__headerbtn .el-dialog__close { color: rgba(255, 255, 255, 0.65); }

  .risk-detail-content { padding: 10px 0; }

  .risk-category {
    margin-bottom: 20px;
    padding: 15px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  .category-title {
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &.severe-title   { color: #FA8C16; }
    &.important-title { color: #FAAD14; }
    &.general-title  { color: #1890FF; }
    &.hijack-title   { color: #F5222D; }
  }

  .risk-item {
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    &:last-child { border-bottom: none; }

    .risk-type       { font-weight: 600; color: #fff; margin-bottom: 4px; }
    .risk-location   { font-size: 13px; color: rgba(255, 255, 255, 0.55); margin-bottom: 4px; }
    .risk-explanation { font-size: 13px; color: rgba(255, 255, 255, 0.75); line-height: 1.6; }
  }

  .hijack-item {
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    &:last-child { border-bottom: none; }

    .hijack-time { font-size: 13px; color: rgba(255, 255, 255, 0.55); margin-bottom: 4px; }
    .hijack-ips  { font-size: 13px; color: rgba(255, 255, 255, 0.85); margin-bottom: 6px; }
    .ip-label    { font-weight: 600; color: rgba(255, 255, 255, 0.55); }
    .hijack-analysis {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.75);
      line-height: 1.8;
      strong { color: rgba(255, 255, 255, 0.9); }
    }
  }
}

.beian-footer {
  grid-area: beian;
  justify-self: stretch;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  flex: 0 0 auto;
  align-self: stretch;
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 180px 0 0;
  padding: 0 0 120px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 13px;

  a {
    color: rgba(255, 255, 255, 0.68);
    text-decoration: none;

    &:hover {
      color: #7ed6ff;
    }
  }
}

.beian-police-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.beian-police-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

</style>

<!-- 全局样式，用于tooltip -->
<style lang="less">
.hijack-purpose-tooltip {
  max-width: min(720px, calc(100vw - 48px)) !important;
  max-height: 360px !important;
  overflow-y: auto !important;
  padding: 14px 16px !important;
  line-height: 1.75 !important;
  font-size: 13px !important;
  white-space: pre-wrap !important;
  word-break: break-word !important;
  color: rgba(255, 255, 255, 0.92) !important;
  background: rgba(7, 18, 42, 0.96) !important;
  border: 1px solid rgba(126, 214, 255, 0.38) !important;
  border-radius: 8px !important;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.36), inset 0 0 18px rgba(24, 144, 255, 0.08) !important;
}

.hijack-purpose-tooltip-content {
  max-width: 100%;
  max-height: 330px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(126, 214, 255, 0.45);
    border-radius: 3px;
  }
}

.risk-desc-tooltip {
  max-width: 400px !important;
  line-height: 1.6 !important;
  font-size: 14px !important;
  padding: 12px 14px !important;
}

.scope-tooltip {
  max-width: 360px !important;
  line-height: 1.6 !important;
  font-size: 13px !important;
  padding: 10px 12px !important;
  color: rgba(255, 255, 255, 0.92) !important;
  background: rgba(7, 18, 42, 0.96) !important;
  border: 1px solid rgba(126, 214, 255, 0.38) !important;
  border-radius: 6px !important;
}
</style>
